import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { 
  calculateBRRRRMetrics, 
  calculateFlipMetrics, 
  calculateBuyHoldMetrics,
  calculateSubjectToMetrics,
  formatCurrency, 
  formatPercent, 
  formatNumber 
} from "@/lib/dealCalculations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Building2, DollarSign, Hammer, Home, RefreshCw, TrendingUp, 
  ChevronDown, ChevronUp, FileText, Settings, LogOut, Crown,
  BarChart3, Calculator, Download, FileSpreadsheet, HelpCircle,
  CheckCircle2, AlertCircle, Loader2, MapPin, Link as LinkIcon, Search,
  Menu, X, Wallet, FileDown
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png";

// Property types for rental analysis
const PROPERTY_TYPES = [
  { value: "single_family", label: "Single Family" },
  { value: "condo", label: "Condo" },
  { value: "townhouse", label: "Townhouse" },
  { value: "apartment", label: "Apartment" },
  { value: "multi_family", label: "Multi-Family (2-4 units)" },
];

// Default deal structures
const defaultBRRRR = {
  name: "New BRRRR Deal",
  property_info: { address: "", city: "", state: "", zip_code: "", listing_price: 0, units: 1, beds: 3, baths: 2, sqft: 0, property_type: "Single Family", property_taxes_annual: 0 },
  acquisition: { purchase_price: 0, closing_costs_percent: 3.0, closing_costs_fixed: 0, loan_amount_percent: 80.0, down_payment_percent: 20.0, interest_rate: 7.0, amortization_years: 30, points_percent: 1.0, lender_fees: 0, short_term_io_enabled: false, short_term_io_include_rehab: true, short_term_io_ltc_percent: 90.0, short_term_io_rate: 12.0, short_term_io_points: 2.0 },
  rehab: { rehab_budget: 0, contingency_percent: 10.0, rehab_timeline_months: 3, holding_payment_type: "interest_only", holding_taxes_monthly: 0, holding_insurance_monthly: 0, holding_utilities_monthly: 0, holding_other_monthly: 0 },
  holding_rent: { market_rent_monthly: 0, other_income_monthly: 0, property_taxes_annual: 0, insurance_annual: 0, utilities_monthly: 0, repairs_percent: 5.0, capex_percent: 8.0, property_management_percent: 8.0, vacancy_percent: 5.0, landscaping_monthly: 0, hoa_monthly: 0, misc_monthly: 0 },
  refinance: { arv: 0, refi_ltv_percent: 75.0, new_interest_rate: 6.5, new_amortization_years: 30, refi_closing_costs_percent: 2.0 },
};

const defaultFlip = {
  name: "New Flip Deal",
  property_info: { address: "", city: "", state: "", zip_code: "", listing_price: 0, units: 1, beds: 3, baths: 2, sqft: 0, property_type: "Single Family", property_taxes_annual: 0 },
  acquisition: { purchase_price: 0, closing_costs_percent: 3.0, closing_costs_fixed: 0, loan_amount_percent: 80.0, down_payment_percent: 20.0, interest_rate: 7.0, amortization_years: 30, points_percent: 1.0, lender_fees: 0, short_term_io_enabled: true, short_term_io_include_rehab: true, short_term_io_ltc_percent: 90.0, short_term_io_rate: 12.0, short_term_io_points: 2.0 },
  rehab: { rehab_budget: 0, contingency_percent: 10.0, rehab_timeline_months: 3, holding_payment_type: "interest_only", holding_taxes_monthly: 0, holding_insurance_monthly: 0, holding_utilities_monthly: 0, holding_other_monthly: 0 },
  sale: { arv: 0, sale_price: 0, selling_costs_percent: 8.0, selling_costs_fixed: 0, holding_time_months: 6 },
};

const defaultBuyHold = {
  name: "New Buy & Hold Deal",
  property_info: { address: "", city: "", state: "", zip_code: "", listing_price: 0, units: 1, beds: 3, baths: 2, sqft: 0, property_type: "Single Family", property_taxes_annual: 0 },
  acquisition: { purchase_price: 0, closing_costs_percent: 3.0, closing_costs_fixed: 0, loan_amount_percent: 80.0, down_payment_percent: 20.0, interest_rate: 7.0, amortization_years: 30, points_percent: 1.0, lender_fees: 0, short_term_io_enabled: false, short_term_io_include_rehab: true, short_term_io_ltc_percent: 90.0, short_term_io_rate: 12.0, short_term_io_points: 2.0 },
  rehab: { rehab_budget: 0, contingency_percent: 10.0, rehab_timeline_months: 0, holding_payment_type: "interest_only", holding_taxes_monthly: 0, holding_insurance_monthly: 0, holding_utilities_monthly: 0, holding_other_monthly: 0 },
  holding_rent: { market_rent_monthly: 0, other_income_monthly: 0, property_taxes_annual: 0, insurance_annual: 0, utilities_monthly: 0, repairs_percent: 5.0, capex_percent: 8.0, property_management_percent: 8.0, vacancy_percent: 5.0, landscaping_monthly: 0, hoa_monthly: 0, misc_monthly: 0 },
};

// Default Subject-To + Seller Finance deal structure
const defaultSubjectTo = {
  name: "New Subject-To Deal",
  property_info: { address: "", city: "", state: "", zip_code: "", beds: 3, baths: 2, sqft: 0, units: 1, property_type: "Single Family", beds_per_unit: 3, baths_per_unit: 2 },
  income: { monthly_rent: 0, other_income_monthly: 0, use_rent_estimate: false },
  subject_to: { 
    purchase_price: 0, 
    closing_costs_percent: 3.0, 
    closing_costs_fixed: 0,
    existing_loan_balance: 0, 
    existing_interest_rate: 6.0, 
    existing_remaining_years: 25,
    existing_monthly_payment: 0 // 0 means auto-calculate
  },
  seller_finance: { 
    down_payment: 0, 
    loan_amount: 0, // 0 means auto-calculate
    auto_calculate: true,
    interest_rate: 6.0, 
    amortization_years: 30,
    monthly_payment_override: 0 // 0 means auto-calculate
  },
  expenses: { 
    property_taxes_annual: 0, 
    property_taxes_monthly: 0,
    insurance_monthly: 0, 
    utilities_monthly: 0, 
    hoa_monthly: 0, 
    other_fixed_monthly: 0 
  },
  reserves: { 
    vacancy_percent: 5, 
    repairs_percent: 5, 
    repairs_use_percent: true,
    repairs_fixed: 0,
    capex_percent: 8, 
    capex_use_percent: true,
    capex_fixed: 0,
    property_management_percent: 8,
    pm_use_percent: true,
    pm_fixed: 0,
    target_coc_return: 10
  },
  cash_invested: { 
    initial_repairs: 0, 
    other_upfront: 0 
  }
};

// Metric display component
const MetricCard = ({ label, value, isCurrency = true, isPercent = false, highlight }) => (
  <div className={`p-3 rounded-lg border ${highlight === 'positive' ? 'border-green-200 bg-green-50' : highlight === 'negative' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-slate-500">{label}</span>
      {highlight === 'positive' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
      {highlight === 'negative' && <AlertCircle className="h-4 w-4 text-red-600" />}
    </div>
    <span className={`font-mono font-semibold ${highlight === 'positive' ? 'text-green-600' : highlight === 'negative' ? 'text-red-600' : 'text-navy-900'}`}>
      {isCurrency ? formatCurrency(value) : isPercent ? formatPercent(value) : formatNumber(value)}
    </span>
  </div>
);

// Inline calculated figures display
const SectionMetrics = ({ items }) => (
  <div className="mt-4 pt-3 border-t border-slate-200">
    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
      {items.map(([label, value], idx) => (
        <div key={idx} className="flex justify-between text-sm py-1">
          <span className="text-slate-500">{label}</span>
          <span className="font-mono font-medium text-slate-900">{value}</span>
        </div>
      ))}
    </div>
  </div>
);

// Number Input field component
const NumberInput = ({ label, value, onChange, prefix, suffix }) => (
  <div className="space-y-1">
    <Label className="text-sm">{label}</Label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <Input
        type="number"
        value={value || ""}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className={`${prefix ? "pl-7" : ""} ${suffix ? "pr-8" : ""} font-mono`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>}
    </div>
  </div>
);

// Text Input field component (for address)
const TextInput = ({ label, value, onChange, placeholder }) => (
  <div className="space-y-1">
    <Label className="text-sm">{label}</Label>
    <Input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="font-normal"
    />
  </div>
);

// Collapsible section
const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="mb-4">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-slate-50 flex flex-row items-center justify-between py-3">
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-teal-500" />
              <CardTitle className="text-base">{title}</CardTitle>
            </div>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default function DealCalculatorsPage({ auth }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("brrrr");
  const [brrrrDeal, setBrrrrDeal] = useState(defaultBRRRR);
  const [flipDeal, setFlipDeal] = useState(defaultFlip);
  const [buyHoldDeal, setBuyHoldDeal] = useState(defaultBuyHold);
  const [subjectToDeal, setSubjectToDeal] = useState(defaultSubjectTo);
  const [brrrrMetrics, setBrrrrMetrics] = useState({});
  const [flipMetrics, setFlipMetrics] = useState({});
  const [buyHoldMetrics, setBuyHoldMetrics] = useState({});
  const [subjectToMetrics, setSubjectToMetrics] = useState({});
  const [rentalAnalysis, setRentalAnalysis] = useState(null);
  const [includeRentalAnalysis, setIncludeRentalAnalysis] = useState(true);
  const [loadingRental, setLoadingRental] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [zillowUrl, setZillowUrl] = useState("");
  const [importingZillow, setImportingZillow] = useState(false);
  const [searchRadius, setSearchRadius] = useState(1); // Default 1 mile radius
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Address autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [activeAddressField, setActiveAddressField] = useState(null); // 'brrrr', 'flip', 'buyhold', or 'subjectto'
  const autocompleteRef = useRef(null);
  const debounceTimer = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load imported property from localStorage (from Dashboard Zillow import)
  useEffect(() => {
    const importedData = localStorage.getItem("zillow_imported_property");
    if (importedData) {
      try {
        const property = JSON.parse(importedData);
        // Check if import is recent (within last hour)
        const importedAt = new Date(property.imported_at);
        const now = new Date();
        const hoursSinceImport = (now - importedAt) / (1000 * 60 * 60);
        
        if (hoursSinceImport < 1) {
          // Apply to all calculators
          applyImportedPropertyToAllCalculators(property);
          toast.success("Zillow property loaded into calculators", {
            description: property.address || "Property data applied"
          });
        }
        // Clear the import after loading
        localStorage.removeItem("zillow_imported_property");
      } catch (e) {
        console.error("Error loading imported property:", e);
      }
    }
  }, []);

  // Apply imported property data to all calculators
  const applyImportedPropertyToAllCalculators = (property) => {
    const propertyInfo = {
      address: property.address || "",
      city: property.city || "",
      state: property.state || "",
      zip_code: property.zip_code || "",
      beds: property.beds || 3,
      baths: property.baths || 2,
      sqft: property.sqft || 0,
      units: 1,
      property_type: property.property_type === "single_family" ? "Single Family" : 
                     property.property_type === "condo" ? "Condo" :
                     property.property_type === "townhouse" ? "Townhouse" :
                     property.property_type === "apartment" ? "Apartment" : "Single Family"
    };

    const purchasePrice = property.price || 0;

    // Update BRRRR
    setBrrrrDeal(prev => ({
      ...prev,
      property_info: { ...prev.property_info, ...propertyInfo },
      acquisition: { ...prev.acquisition, purchase_price: purchasePrice },
      refinance: { ...prev.refinance, arv: purchasePrice }
    }));

    // Update Flip
    setFlipDeal(prev => ({
      ...prev,
      property_info: { ...prev.property_info, ...propertyInfo },
      acquisition: { ...prev.acquisition, purchase_price: purchasePrice },
      sale: { ...prev.sale, arv: purchasePrice, sale_price: purchasePrice }
    }));

    // Update Buy & Hold
    setBuyHoldDeal(prev => ({
      ...prev,
      property_info: { ...prev.property_info, ...propertyInfo },
      acquisition: { ...prev.acquisition, purchase_price: purchasePrice }
    }));

    // Update Subject-To
    setSubjectToDeal(prev => ({
      ...prev,
      property_info: { 
        ...prev.property_info, 
        ...propertyInfo,
        beds_per_unit: propertyInfo.beds,
        baths_per_unit: propertyInfo.baths
      },
      subject_to: { ...prev.subject_to, purchase_price: purchasePrice }
    }));
  };
  // Fetch address suggestions
  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/address/autocomplete?q=${encodeURIComponent(query)}&limit=5`);
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Autocomplete error:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Handle address input change with debounce
  const handleAddressChange = (value, setter, fieldName) => {
    setter(prev => ({...prev, property_info: {...prev.property_info, address: value}}));
    setActiveAddressField(fieldName);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  // Select a suggestion and auto-fill address fields
  const selectSuggestion = (suggestion, setter) => {
    setter(prev => ({
      ...prev,
      property_info: {
        ...prev.property_info,
        address: suggestion.address || suggestion.display_name?.split(",")[0] || "",
        city: suggestion.city || "",
        state: suggestion.state || "",
        zip_code: suggestion.zip_code || "",
      }
    }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Calculate metrics on deal changes
  useEffect(() => { setBrrrrMetrics(calculateBRRRRMetrics(brrrrDeal)); }, [brrrrDeal]);
  useEffect(() => { setFlipMetrics(calculateFlipMetrics(flipDeal)); }, [flipDeal]);
  useEffect(() => { setBuyHoldMetrics(calculateBuyHoldMetrics(buyHoldDeal)); }, [buyHoldDeal]);
  useEffect(() => { setSubjectToMetrics(calculateSubjectToMetrics(subjectToDeal)); }, [subjectToDeal]);

  const getCurrentDeal = () => {
    if (activeTab === "brrrr") return { deal: brrrrDeal, metrics: brrrrMetrics, type: "BRRRR", setter: setBrrrrDeal };
    if (activeTab === "flip") return { deal: flipDeal, metrics: flipMetrics, type: "Fix & Flip", setter: setFlipDeal };
    if (activeTab === "subjectto") return { deal: subjectToDeal, metrics: subjectToMetrics, type: "Subject-To + Seller Finance", setter: setSubjectToDeal };
    return { deal: buyHoldDeal, metrics: buyHoldMetrics, type: "Buy & Hold", setter: setBuyHoldDeal };
  };

  const updateDeal = (setter) => (section, field, value) => {
    setter(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  // Run rental analysis for Subject-To calculator
  const runSubjectToRentalAnalysis = async () => {
    const address = subjectToDeal.property_info.address;
    const city = subjectToDeal.property_info.city;
    const state = subjectToDeal.property_info.state;
    const zip = subjectToDeal.property_info.zip_code;
    
    if (!address && !zip) {
      toast.error("Please enter an address or zip code first");
      return;
    }

    setLoadingRental(true);
    try {
      // Use address endpoint if we have address, otherwise use zip
      const endpoint = address ? `${API}/analysis/address` : `${API}/analysis/zip`;
      const payload = address ? {
        address: address,
        city: city,
        state: state,
        zip_code: zip,
        bedrooms: subjectToDeal.property_info.beds || 3,
        bathrooms: subjectToDeal.property_info.baths || 2,
        property_type: subjectToDeal.property_info.property_type || "Single Family",
      } : {
        zip_code: zip,
        bedrooms: subjectToDeal.property_info.beds || 3,
        bathrooms: subjectToDeal.property_info.baths || 2,
        property_type: subjectToDeal.property_info.property_type || "Single Family",
      };

      const response = await axios.post(endpoint, payload, { withCredentials: true });

      const rentEstimate = response.data.rent_estimate?.estimated_rent || 
                          response.data.rent_estimate?.rent_average || 0;
      
      if (rentEstimate > 0) {
        setSubjectToDeal(prev => ({
          ...prev,
          income: { ...prev.income, monthly_rent: rentEstimate, use_rent_estimate: true }
        }));
        setRentalAnalysis(response.data);
        toast.success(`Rent estimate: ${formatCurrency(rentEstimate)}/month`);
      } else {
        toast.error("Could not get rent estimate for this property");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail?.redirect) {
        toast.error(detail.message || "Please subscribe to use this feature");
        navigate(detail.redirect);
      } else {
        toast.error(typeof detail === 'string' ? detail : "Failed to get rent estimate");
      }
    } finally {
      setLoadingRental(false);
    }
  };

  // Import from Zillow URL
  const handleZillowImport = async () => {
    if (!zillowUrl || !zillowUrl.toLowerCase().includes('zillow.com')) {
      toast.error("Please enter a valid Zillow URL");
      return;
    }

    setImportingZillow(true);
    try {
      const response = await axios.post(`${API}/scrape/zillow`, {
        url: zillowUrl
      }, { withCredentials: true });
      
      const data = response.data;
      
      // Apply to ALL calculators, not just the current one
      applyImportedPropertyToAllCalculators(data);
      
      toast.success("Property imported to all calculators!", {
        description: `${data.address || "Property"} - ${data.beds || 0} bed, ${data.baths || 0} bath${data.price ? ` • $${data.price.toLocaleString()}` : ""}`
      });
      setZillowUrl("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import from Zillow. Please enter details manually.");
    } finally {
      setImportingZillow(false);
    }
  };

  // Fetch rental analysis
  const fetchRentalAnalysis = async () => {
    const { deal, setter } = getCurrentDeal();
    const address = deal.property_info?.address;
    if (!address || address.trim().length < 5) {
      toast.error("Please enter a valid property address first");
      return;
    }
    
    // Calculate beds and baths per unit for multi-family
    const units = deal.property_info?.units || 1;
    const bedsPerUnit = Math.round((deal.property_info?.beds || 3) / units);
    const bathsPerUnit = Math.round(((deal.property_info?.baths || 2) / units) * 10) / 10; // Round to 1 decimal
    
    setLoadingRental(true);
    try {
      const response = await axios.post(`${API}/analysis/address`, {
        address: address,
        property_type: deal.property_info?.property_type?.toLowerCase().replace(/ /g, "_") || "single_family",
        bedrooms: bedsPerUnit,
        bathrooms: bathsPerUnit,
        sqft: deal.property_info?.sqft ? Math.round(deal.property_info.sqft / units) : null,
        search_radius_miles: searchRadius,
      }, { withCredentials: true });
      
      setRentalAnalysis(response.data);
      setIncludeRentalAnalysis(true);
      
      // Auto-fill rent if applicable for BRRRR, Buy & Hold, or Subject-To (multiply by units for multi-family)
      if (response.data.rent_estimate?.estimated_rent) {
        const rentPerUnit = response.data.rent_estimate.estimated_rent;
        const totalRent = rentPerUnit * units;
        if (activeTab === "brrrr" || activeTab === "buyhold") {
          setter(prev => ({ 
            ...prev, 
            holding_rent: { ...prev.holding_rent, market_rent_monthly: totalRent },
            property_info: {
              ...prev.property_info,
              city: response.data.city || prev.property_info.city,
              state: response.data.state || prev.property_info.state,
              zip_code: response.data.zip_code || prev.property_info.zip_code,
            }
          }));
        } else if (activeTab === "subjectto") {
          setter(prev => ({ 
            ...prev, 
            income: { ...prev.income, monthly_rent: totalRent, use_rent_estimate: true },
            property_info: {
              ...prev.property_info,
              city: response.data.city || prev.property_info.city,
              state: response.data.state || prev.property_info.state,
              zip_code: response.data.zip_code || prev.property_info.zip_code,
            }
          }));
        }
      }
      
      toast.success("Rental analysis complete! Rent estimate applied.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get rental analysis. Check the address.");
    } finally {
      setLoadingRental(false);
    }
  };

  // Export to PDF with logo and branding
  const exportToPDF = async () => {
    setExportingPdf(true);
    const { deal, metrics, type } = getCurrentDeal();
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;
      
      // Header with light gray background
      pdf.setFillColor(241, 245, 249); // slate-100
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Try to load and add logo, fallback to text if it fails
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 3000); // 3 second timeout
          logoImg.src = LOGO_URL;
        });
        
        pdf.addImage(logoImg, 'PNG', margin, 8, 50, 24);
      } catch (logoError) {
        // Fallback to text logo if image fails
        pdf.setTextColor(13, 148, 136); // teal-600
        pdf.setFontSize(24);
        pdf.setFont('helvetica', 'bold');
        pdf.text('RENTALYTIX', margin, 24);
      }
      
      // Report title
      pdf.setTextColor(15, 23, 42); // navy-900
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${type} Deal Analysis`, pageWidth - margin, 18, { align: 'right' });
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 116, 139); // slate-500
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - margin, 28, { align: 'right' });
      
      yPos = 50;
      
      // Property Info Section
      pdf.setFillColor(241, 245, 249);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 30, 3, 3, 'F');
      yPos += 8;
      
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Property Details', margin + 5, yPos);
      yPos += 7;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      const address = deal.property_info?.address || 'Address not provided';
      pdf.text(address, margin + 5, yPos);
      yPos += 5;
      
      const cityStateZip = [deal.property_info?.city, deal.property_info?.state, deal.property_info?.zip_code].filter(Boolean).join(', ');
      if (cityStateZip) pdf.text(cityStateZip, margin + 5, yPos);
      yPos += 5;
      
      pdf.text(`${deal.property_info?.property_type || 'Single Family'} | ${deal.property_info?.beds || 0} Beds | ${deal.property_info?.baths || 0} Baths`, margin + 5, yPos);
      
      yPos += 18;
      
      // Key Metrics Section - calculate height based on content
      const keyMetricsHeight = 60;
      pdf.setFillColor(240, 253, 250); // teal-50
      pdf.setDrawColor(45, 212, 191); // teal-400
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), keyMetricsHeight, 3, 3, 'FD');
      yPos += 8;
      
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Key Investment Metrics', margin + 5, yPos);
      yPos += 12;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Define column positions for labels and values
      const labelCol1 = margin + 5;
      const valueCol1 = margin + 55;
      const labelCol2 = margin + 100;
      const valueCol2 = margin + 150;
      
      if (type === "BRRRR") {
        // Row 1
        pdf.text(`Purchase Price:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(deal.acquisition?.purchase_price), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`ARV:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(deal.refinance?.arv), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 2
        pdf.text(`Total Cash Invested:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.total_cash_before_refi), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Cash Out Refi:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(metrics.cash_out_from_refi > 0 ? 22 : 220, metrics.cash_out_from_refi > 0 ? 163 : 38, metrics.cash_out_from_refi > 0 ? 74 : 38);
        pdf.text(formatCurrency(metrics.cash_out_from_refi), valueCol2, yPos);
        pdf.setTextColor(15, 23, 42);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 3
        pdf.text(`Monthly Cash Flow:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(metrics.monthly_cash_flow > 0 ? 22 : 220, metrics.monthly_cash_flow > 0 ? 163 : 38, metrics.monthly_cash_flow > 0 ? 74 : 38);
        pdf.text(formatCurrency(metrics.monthly_cash_flow), valueCol1, yPos);
        pdf.setTextColor(15, 23, 42);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Cash-on-Cash:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatPercent(metrics.cash_on_cash_return), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 4
        pdf.text(`NOI:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.noi), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Cap Rate:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatPercent(metrics.cap_rate_arv), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        
      } else if (type === "Fix & Flip") {
        // Row 1
        pdf.text(`Purchase Price:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(deal.acquisition?.purchase_price), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Sale Price (ARV):`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(deal.sale?.arv), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 2
        pdf.text(`Total Cash Invested:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.total_cash_invested), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Project Cost:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.total_project_cost), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 3
        pdf.text(`Net Profit:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(metrics.net_profit > 0 ? 22 : 220, metrics.net_profit > 0 ? 163 : 38, metrics.net_profit > 0 ? 74 : 38);
        pdf.text(formatCurrency(metrics.net_profit), valueCol1, yPos);
        pdf.setTextColor(15, 23, 42);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`ROI:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatPercent(metrics.roi_percent), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 4
        pdf.text(`Annualized ROI:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatPercent(metrics.annualized_roi), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Profit Margin:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatPercent(metrics.profit_margin), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        
      } else { // Buy & Hold
        // Row 1
        pdf.text(`Purchase Price:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(deal.acquisition?.purchase_price), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Property Value:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.total_property_value), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 2
        pdf.text(`Total Cash Invested:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.total_cash_invested), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Equity:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.equity), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 3
        pdf.text(`Monthly Cash Flow:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(metrics.monthly_cash_flow > 0 ? 22 : 220, metrics.monthly_cash_flow > 0 ? 163 : 38, metrics.monthly_cash_flow > 0 ? 74 : 38);
        pdf.text(formatCurrency(metrics.monthly_cash_flow), valueCol1, yPos);
        pdf.setTextColor(15, 23, 42);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Cash-on-Cash:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatPercent(metrics.cash_on_cash_return), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += 7;
        
        // Row 4
        pdf.text(`NOI:`, labelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.noi), valueCol1, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Cap Rate:`, labelCol2, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatPercent(metrics.cap_rate), valueCol2, yPos);
        pdf.setFont('helvetica', 'normal');
      }
      
      yPos += 25;
      
      // Rental Analysis Section (if included)
      if (includeRentalAnalysis && rentalAnalysis) {
        pdf.setFillColor(245, 243, 255); // purple-50
        pdf.setDrawColor(139, 92, 246); // purple-500
        pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 50, 3, 3, 'FD');
        yPos += 8;
        
        pdf.setTextColor(88, 28, 135); // purple-900
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Rental Market Analysis', margin + 5, yPos);
        yPos += 10;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        
        const raLabelCol1 = margin + 5;
        const raValueCol1 = margin + 55;
        const raLabelCol2 = margin + 100;
        const raValueCol2 = margin + 145;
        
        // Row 1
        pdf.text(`Est. Monthly Rent:`, raLabelCol1, yPos);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(139, 92, 246);
        pdf.text(formatCurrency(rentalAnalysis.rent_estimate?.estimated_rent), raValueCol1, yPos);
        pdf.setTextColor(71, 85, 105);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Market Range:`, raLabelCol2, yPos);
        pdf.text(`${formatCurrency(rentalAnalysis.rent_estimate?.rent_low)} - ${formatCurrency(rentalAnalysis.rent_estimate?.rent_high)}`, raValueCol2, yPos);
        yPos += 7;
        
        // Row 2
        pdf.text(`Market Average:`, raLabelCol1, yPos);
        pdf.text(formatCurrency(rentalAnalysis.rent_estimate?.rent_average), raValueCol1, yPos);
        pdf.text(`Median Rent:`, raLabelCol2, yPos);
        pdf.text(formatCurrency(rentalAnalysis.rent_estimate?.rent_median), raValueCol2, yPos);
        yPos += 7;
        
        // Row 3
        pdf.text(`Comparables:`, raLabelCol1, yPos);
        pdf.text(`${rentalAnalysis.rent_estimate?.comps_count || rentalAnalysis.comparables?.length || 0}`, raValueCol1, yPos);
        pdf.text(`Search Radius:`, raLabelCol2, yPos);
        pdf.text(`${rentalAnalysis.search_radius_miles || 1} miles`, raValueCol2, yPos);
        
        yPos += 18;
      }
      
      // Acquisition Details Section - Two columns with proper spacing
      const sectionHeight = 60;
      const colWidth = (pageWidth - (margin * 2) - 6) / 2;
      
      pdf.setFillColor(241, 245, 249);
      pdf.roundedRect(margin, yPos, colWidth, sectionHeight, 3, 3, 'F');
      
      const rightColX = margin + colWidth + 6;
      pdf.roundedRect(rightColX, yPos, colWidth, sectionHeight, 3, 3, 'F');
      
      // Left column - Acquisition
      let leftY = yPos + 8;
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Acquisition', margin + 5, leftY);
      leftY += 9;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      
      const acqLabelX = margin + 5;
      const acqValueX = margin + 45;
      
      pdf.text(`Down Payment:`, acqLabelX, leftY);
      pdf.text(formatCurrency(metrics.down_payment), acqValueX, leftY);
      leftY += 6;
      pdf.text(`Loan Amount:`, acqLabelX, leftY);
      pdf.text(formatCurrency(metrics.loan_amount), acqValueX, leftY);
      leftY += 6;
      pdf.text(`Closing Costs:`, acqLabelX, leftY);
      pdf.text(formatCurrency(metrics.closing_costs), acqValueX, leftY);
      leftY += 6;
      pdf.text(`Points/Fees:`, acqLabelX, leftY);
      pdf.text(formatCurrency(metrics.points_fees), acqValueX, leftY);
      leftY += 6;
      pdf.text(`Interest Rate:`, acqLabelX, leftY);
      pdf.text(`${deal.acquisition?.interest_rate || 0}%`, acqValueX, leftY);
      leftY += 6;
      pdf.text(`Cash at Closing:`, acqLabelX, leftY);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatCurrency(metrics.total_cash_at_closing), acqValueX, leftY);
      pdf.setFont('helvetica', 'normal');
      
      // Right column - Rehab or Income
      let rightY = yPos + 8;
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      
      const rightLabelX = rightColX + 5;
      const rightValueX = rightColX + 50;
      
      if (type === "Fix & Flip") {
        pdf.text('Rehab & Sale', rightLabelX, rightY);
        rightY += 9;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        pdf.text(`Rehab Budget:`, rightLabelX, rightY);
        pdf.text(formatCurrency(deal.rehab?.rehab_budget), rightValueX, rightY);
        rightY += 6;
        pdf.text(`Total Rehab:`, rightLabelX, rightY);
        pdf.text(formatCurrency(metrics.total_rehab_cost), rightValueX, rightY);
        rightY += 6;
        pdf.text(`Holding Costs:`, rightLabelX, rightY);
        pdf.text(formatCurrency(metrics.holding_costs_total), rightValueX, rightY);
        rightY += 6;
        pdf.text(`Selling Costs:`, rightLabelX, rightY);
        pdf.text(formatCurrency(metrics.selling_costs), rightValueX, rightY);
        rightY += 6;
        pdf.text(`Net Proceeds:`, rightLabelX, rightY);
        pdf.text(formatCurrency(metrics.net_sale_proceeds), rightValueX, rightY);
      } else {
        pdf.text('Income & Expenses', rightLabelX, rightY);
        rightY += 9;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        pdf.text(`Monthly Rent:`, rightLabelX, rightY);
        pdf.text(formatCurrency(deal.holding_rent?.market_rent_monthly), rightValueX, rightY);
        rightY += 6;
        pdf.text(`Gross Annual:`, rightLabelX, rightY);
        pdf.text(formatCurrency(metrics.gross_scheduled_rent), rightValueX, rightY);
        rightY += 6;
        pdf.text(`Expenses:`, rightLabelX, rightY);
        pdf.text(formatCurrency(metrics.total_operating_expenses), rightValueX, rightY);
        rightY += 6;
        pdf.text(`Vacancy:`, rightLabelX, rightY);
        pdf.text(`${deal.holding_rent?.vacancy_percent || 0}%`, rightValueX, rightY);
        rightY += 6;
        pdf.text(`NOI:`, rightLabelX, rightY);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(metrics.noi), rightValueX, rightY);
        pdf.setFont('helvetica', 'normal');
      }
      
      // Footer
      const footerY = pageHeight - 15;
      pdf.setDrawColor(226, 232, 240);
      pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      pdf.setFontSize(8);
      pdf.setTextColor(148, 163, 184);
      pdf.text('Rentlytix - Rental Analysis & Deal Calculator', margin, footerY);
      pdf.text('www.rentlytix.com', pageWidth - margin, footerY, { align: 'right' });
      
      pdf.save(`rentlytix_${type.toLowerCase().replace(/\s+/g, '_')}_${deal.property_info?.address?.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_') || 'analysis'}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    setExportingExcel(true);
    const { deal, metrics, type } = getCurrentDeal();
    
    try {
      const workbook = XLSX.utils.book_new();
      
      // Summary sheet
      const summaryData = [
        ["RENTALYTIX - DEAL ANALYSIS REPORT"],
        [""],
        ["Report Type", type],
        ["Generated", new Date().toLocaleString()],
        [""],
        ["PROPERTY DETAILS"],
        ["Address", deal.property_info?.address || ""],
        ["City", deal.property_info?.city || ""],
        ["State", deal.property_info?.state || ""],
        ["ZIP Code", deal.property_info?.zip_code || ""],
        ["Property Type", deal.property_info?.property_type || ""],
        ["Bedrooms", deal.property_info?.beds || 0],
        ["Bathrooms", deal.property_info?.baths || 0],
        ["Square Feet", deal.property_info?.sqft || 0],
        [""],
        ["ACQUISITION"],
        ["Purchase Price", deal.acquisition?.purchase_price || 0],
        ["Down Payment", metrics.down_payment || 0],
        ["Loan Amount", metrics.loan_amount || 0],
        ["Closing Costs", metrics.closing_costs || 0],
        ["Points/Fees", metrics.points_fees || 0],
        ["Interest Rate", `${deal.acquisition?.interest_rate || 0}%`],
        ["Loan Term (years)", deal.acquisition?.amortization_years || 30],
        [""],
        ["REHAB"],
        ["Rehab Budget", deal.rehab?.rehab_budget || 0],
        ["Contingency %", `${deal.rehab?.contingency_percent || 0}%`],
        ["Total Rehab Cost", metrics.total_rehab_cost || 0],
        [""],
        ["KEY METRICS"],
      ];
      
      if (type === "BRRRR") {
        summaryData.push(
          ["Total Cash Before Refi", metrics.total_cash_before_refi],
          ["Cash Out from Refi", metrics.cash_out_from_refi],
          ["Cash Left in Deal", metrics.cash_left_in_deal],
          ["Equity Created", metrics.equity_created],
          ["Monthly Cash Flow", metrics.monthly_cash_flow],
          ["Annual Cash Flow", metrics.annual_cash_flow],
          ["Cash-on-Cash Return", `${formatNumber(metrics.cash_on_cash_return)}%`],
          ["NOI", metrics.noi],
          ["Cap Rate (ARV)", `${formatNumber(metrics.cap_rate_arv)}%`],
          ["DSCR", formatNumber(metrics.dscr)],
          ["Break-Even Occupancy", `${formatNumber(metrics.break_even_occupancy)}%`],
        );
      } else if (type === "Fix & Flip") {
        summaryData.push(
          ["Total Cash Invested", metrics.total_cash_invested],
          ["Total Project Cost", metrics.total_project_cost],
          ["Gross Sale Price", metrics.gross_sale_price],
          ["Selling Costs", metrics.selling_costs],
          ["Net Sale Proceeds", metrics.net_sale_proceeds],
          ["Gross Profit", metrics.gross_profit],
          ["Net Profit", metrics.net_profit],
          ["ROI", `${formatNumber(metrics.roi_percent)}%`],
          ["Annualized ROI", `${formatNumber(metrics.annualized_roi)}%`],
          ["Profit Margin", `${formatNumber(metrics.profit_margin)}%`],
        );
      } else {
        summaryData.push(
          ["Total Cash Invested", metrics.total_cash_invested],
          ["Total Property Value", metrics.total_property_value],
          ["Equity", metrics.equity],
          ["Monthly Cash Flow", metrics.monthly_cash_flow],
          ["Annual Cash Flow", metrics.annual_cash_flow],
          ["Cash-on-Cash Return", `${formatNumber(metrics.cash_on_cash_return)}%`],
          ["NOI", metrics.noi],
          ["Cap Rate", `${formatNumber(metrics.cap_rate)}%`],
          ["DSCR", formatNumber(metrics.dscr)],
          ["Break-Even Occupancy", `${formatNumber(metrics.break_even_occupancy)}%`],
        );
      }
      
      // Add rental analysis if included
      if (includeRentalAnalysis && rentalAnalysis) {
        summaryData.push(
          [""],
          ["RENTAL MARKET ANALYSIS"],
          ["Estimated Monthly Rent", rentalAnalysis.rent_estimate?.estimated_rent || 0],
          ["Market Low", rentalAnalysis.rent_estimate?.rent_low || 0],
          ["Market High", rentalAnalysis.rent_estimate?.rent_high || 0],
          ["Market Average", rentalAnalysis.rent_estimate?.rent_average || 0],
          ["Market Median", rentalAnalysis.rent_estimate?.rent_median || 0],
          ["25th Percentile", rentalAnalysis.rent_estimate?.percentile_25 || 0],
          ["75th Percentile", rentalAnalysis.rent_estimate?.percentile_75 || 0],
          ["Comparable Properties", rentalAnalysis.rent_estimate?.comps_count || rentalAnalysis.comparables?.length || 0],
        );
        
        // Add comparables sheet if available
        if (rentalAnalysis.comparables?.length > 0) {
          const compsData = [["Address", "Rent", "Beds", "Baths", "Sqft", "Property Type", "Distance (mi)"]];
          rentalAnalysis.comparables.forEach(comp => {
            compsData.push([
              comp.address || "",
              comp.rent || 0,
              comp.bedrooms || 0,
              comp.bathrooms || 0,
              comp.sqft || 0,
              comp.property_type || "",
              comp.distance_miles?.toFixed(2) || 0,
            ]);
          });
          const compsSheet = XLSX.utils.aoa_to_sheet(compsData);
          XLSX.utils.book_append_sheet(workbook, compsSheet, "Comparable Properties");
        }
      }
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Deal Analysis");
      
      const filename = `rentlytix_${type.toLowerCase().replace(/\s+/g, '_')}_${deal.property_info?.address?.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_') || 'analysis'}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success("Excel file exported successfully!");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setExportingExcel(false);
    }
  };

  // Export Subject-To to PDF
  const exportSubjectToPDF = async () => {
    setExportingPdf(true);
    const deal = subjectToDeal;
    const metrics = subjectToMetrics;
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = margin;
      
      // Header
      pdf.setFillColor(241, 245, 249);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      pdf.setFontSize(20);
      pdf.setTextColor(15, 23, 42);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RENTALYTIX', margin, 24);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Subject-To + Seller Finance Analysis | ${new Date().toLocaleDateString()}`, margin, 33);
      
      yPos = 50;
      
      // Property Info
      pdf.setFillColor(241, 245, 249);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 25, 3, 3, 'F');
      yPos += 8;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Property Details', margin + 5, yPos);
      yPos += 7;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      pdf.text(deal.property_info?.address || 'Address not provided', margin + 5, yPos);
      yPos += 5;
      pdf.text(`${deal.property_info?.property_type || 'Single Family'} | ${deal.property_info?.beds || 0} Beds | ${deal.property_info?.baths || 0} Baths | ${deal.property_info?.units || 1} Unit(s)`, margin + 5, yPos);
      
      yPos += 18;
      
      // Deal Quality Indicator
      const isDeal = metrics.is_deal;
      pdf.setFillColor(isDeal ? 220 : 254, isDeal ? 252 : 226, isDeal ? 231 : 226);
      pdf.setDrawColor(isDeal ? 34 : 239, isDeal ? 197 : 68, isDeal ? 94 : 68);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 20, 3, 3, 'FD');
      yPos += 12;
      
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(isDeal ? 22 : 185, isDeal ? 163 : 28, isDeal ? 74 : 28);
      pdf.text(isDeal ? 'DEAL' : 'NO DEAL', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 18;
      
      // Key Metrics Box
      pdf.setFillColor(240, 253, 250);
      pdf.setDrawColor(45, 212, 191);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 50, 3, 3, 'FD');
      yPos += 8;
      
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Key Investment Returns', margin + 5, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      const col1 = margin + 5;
      const val1 = margin + 55;
      const col2 = margin + 100;
      const val2 = margin + 150;
      
      pdf.setFont('helvetica', 'normal');
      pdf.text('Monthly Cash Flow:', col1, yPos);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(metrics.monthly_cash_flow >= 0 ? 22 : 185, metrics.monthly_cash_flow >= 0 ? 163 : 28, metrics.monthly_cash_flow >= 0 ? 74 : 28);
      pdf.text(formatCurrency(metrics.monthly_cash_flow), val1, yPos);
      pdf.setTextColor(15, 23, 42);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Annual Cash Flow:', col2, yPos);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatCurrency(metrics.annual_cash_flow), val2, yPos);
      pdf.setFont('helvetica', 'normal');
      yPos += 7;
      
      pdf.text('Cash-on-Cash Return:', col1, yPos);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatPercent(metrics.cash_on_cash_return), val1, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text('DSCR:', col2, yPos);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatNumber(metrics.dscr), val2, yPos);
      pdf.setFont('helvetica', 'normal');
      yPos += 7;
      
      pdf.text('Total Cash Invested:', col1, yPos);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatCurrency(metrics.total_cash_invested), val1, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text('NOI (Monthly):', col2, yPos);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatCurrency(metrics.noi), val2, yPos);
      
      yPos += 20;
      
      // Two column layout for financing details
      const colWidth = (pageWidth - (margin * 2) - 6) / 2;
      
      // Left: Financing
      pdf.setFillColor(241, 245, 249);
      pdf.roundedRect(margin, yPos, colWidth, 55, 3, 3, 'F');
      
      let leftY = yPos + 8;
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Financing Structure', margin + 5, leftY);
      leftY += 8;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      
      const finCol = margin + 5;
      const finVal = margin + 45;
      
      pdf.text('Purchase Price:', finCol, leftY);
      pdf.text(formatCurrency(metrics.purchase_price), finVal, leftY);
      leftY += 6;
      pdf.text('Subject-To Loan:', finCol, leftY);
      pdf.text(formatCurrency(metrics.existing_loan_balance), finVal, leftY);
      leftY += 6;
      pdf.text('Seller Finance:', finCol, leftY);
      pdf.text(formatCurrency(metrics.seller_finance_amount), finVal, leftY);
      leftY += 6;
      pdf.text('Down Payment:', finCol, leftY);
      pdf.text(formatCurrency(metrics.down_payment), finVal, leftY);
      leftY += 6;
      pdf.text('Closing Costs:', finCol, leftY);
      pdf.text(formatCurrency(metrics.closing_costs), finVal, leftY);
      leftY += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Your Equity:', finCol, leftY);
      pdf.text(formatCurrency(metrics.total_equity), finVal, leftY);
      
      // Right: Monthly Breakdown
      const rightColX = margin + colWidth + 6;
      pdf.setFillColor(241, 245, 249);
      pdf.roundedRect(rightColX, yPos, colWidth, 55, 3, 3, 'F');
      
      let rightY = yPos + 8;
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Monthly Breakdown', rightColX + 5, rightY);
      rightY += 8;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      
      const brkCol = rightColX + 5;
      const brkVal = rightColX + 50;
      
      pdf.text('Gross Income:', brkCol, rightY);
      pdf.text(formatCurrency(metrics.gross_scheduled_rent), brkVal, rightY);
      rightY += 6;
      pdf.text('Operating Exp:', brkCol, rightY);
      pdf.text(`-${formatCurrency(metrics.total_operating_expenses)}`, brkVal, rightY);
      rightY += 6;
      pdf.text('NOI:', brkCol, rightY);
      pdf.text(formatCurrency(metrics.noi), brkVal, rightY);
      rightY += 6;
      pdf.text('Subject-To Pmt:', brkCol, rightY);
      pdf.text(`-${formatCurrency(metrics.subject_to_payment)}`, brkVal, rightY);
      rightY += 6;
      pdf.text('Seller Finance Pmt:', brkCol, rightY);
      pdf.text(`-${formatCurrency(metrics.seller_finance_payment)}`, brkVal, rightY);
      rightY += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Cash Flow:', brkCol, rightY);
      pdf.setTextColor(metrics.monthly_cash_flow >= 0 ? 22 : 185, metrics.monthly_cash_flow >= 0 ? 163 : 28, metrics.monthly_cash_flow >= 0 ? 74 : 28);
      pdf.text(formatCurrency(metrics.monthly_cash_flow), brkVal, rightY);
      
      // Footer
      const footerY = pdf.internal.pageSize.getHeight() - 10;
      pdf.setFontSize(8);
      pdf.setTextColor(148, 163, 184);
      pdf.text('Rentlytix - Subject-To + Seller Finance Calculator', margin, footerY);
      pdf.text('www.rentlytix.com', pageWidth - margin, footerY, { align: 'right' });
      
      pdf.save(`rentlytix_subject_to_${deal.property_info?.address?.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_') || 'analysis'}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  // Export Subject-To to Excel
  const exportSubjectToExcel = () => {
    setExportingExcel(true);
    const deal = subjectToDeal;
    const metrics = subjectToMetrics;
    
    try {
      const workbook = XLSX.utils.book_new();
      
      const summaryData = [
        ["RENTALYTIX - SUBJECT-TO + SELLER FINANCE ANALYSIS"],
        [""],
        ["Generated", new Date().toLocaleString()],
        ["Deal Status", metrics.is_deal ? "DEAL" : "NO DEAL"],
        [""],
        ["PROPERTY DETAILS"],
        ["Address", deal.property_info?.address || ""],
        ["City", deal.property_info?.city || ""],
        ["State", deal.property_info?.state || ""],
        ["ZIP Code", deal.property_info?.zip_code || ""],
        ["Property Type", deal.property_info?.property_type || ""],
        ["Units", deal.property_info?.units || 1],
        ["Bedrooms", deal.property_info?.beds || 0],
        ["Bathrooms", deal.property_info?.baths || 0],
        [""],
        ["INCOME"],
        ["Monthly Rent", metrics.monthly_rent || 0],
        ["Other Monthly Income", metrics.other_income || 0],
        ["Gross Scheduled Rent", metrics.gross_scheduled_rent || 0],
        ["Vacancy Reserve", metrics.vacancy_reserve || 0],
        ["Effective Gross Income", metrics.effective_gross_income || 0],
        [""],
        ["SUBJECT-TO MORTGAGE"],
        ["Existing Loan Balance", metrics.existing_loan_balance || 0],
        ["Interest Rate", `${deal.subject_to?.existing_interest_rate || 0}%`],
        ["Remaining Term (years)", deal.subject_to?.existing_remaining_years || 0],
        ["Monthly Payment", metrics.subject_to_payment || 0],
        [""],
        ["SELLER FINANCE NOTE"],
        ["Down Payment", metrics.down_payment || 0],
        ["Seller Finance Amount", metrics.seller_finance_amount || 0],
        ["Interest Rate", `${deal.seller_finance?.interest_rate || 0}%`],
        ["Amortization (years)", deal.seller_finance?.amortization_years || 30],
        ["Monthly Payment", metrics.seller_finance_payment || 0],
        [""],
        ["OPERATING EXPENSES (MONTHLY)"],
        ["Property Taxes", metrics.property_taxes_monthly || 0],
        ["Insurance", metrics.insurance_monthly || 0],
        ["Utilities", metrics.utilities_monthly || 0],
        ["HOA", metrics.hoa_monthly || 0],
        ["Other Fixed", metrics.other_fixed_monthly || 0],
        ["Repairs", metrics.repairs_monthly || 0],
        ["CapEx", metrics.capex_monthly || 0],
        ["Property Management", metrics.pm_monthly || 0],
        ["Total Operating Expenses", metrics.total_operating_expenses || 0],
        [""],
        ["CASH INVESTED"],
        ["Down Payment", metrics.down_payment || 0],
        ["Closing Costs", metrics.closing_costs || 0],
        ["Initial Repairs", metrics.initial_repairs || 0],
        ["Other Upfront", metrics.other_upfront || 0],
        ["Total Cash Invested", metrics.total_cash_invested || 0],
        [""],
        ["KEY RETURNS"],
        ["Net Operating Income (Monthly)", metrics.noi || 0],
        ["Total Monthly Debt Service", metrics.total_monthly_debt_service || 0],
        ["Monthly Cash Flow", metrics.monthly_cash_flow || 0],
        ["Annual Cash Flow", metrics.annual_cash_flow || 0],
        ["Cash-on-Cash Return", `${(metrics.cash_on_cash_return || 0).toFixed(2)}%`],
        ["DSCR", (metrics.dscr || 0).toFixed(2)],
        ["Your Equity", metrics.total_equity || 0],
      ];
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Subject-To Analysis");
      
      const filename = `rentlytix_subject_to_${deal.property_info?.address?.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_') || 'analysis'}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success("Excel file exported successfully!");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setExportingExcel(false);
    }
  };

  const tierBadgeColor = {
    trial: "bg-slate-100 text-slate-700",
    basic: "bg-teal-100 text-teal-700",
    pro: "bg-purple-100 text-purple-700",
    admin: "bg-amber-100 text-amber-700"
  };

  const currentTier = auth.user?.subscription_tier || "trial";

  // Rental Analysis Display Component
  const RentalAnalysisCard = ({ showButton = true }) => {
    const { deal, setter } = getCurrentDeal();
    const units = deal?.property_info?.units || 1;
    const bedsPerUnit = units > 1 ? Math.round((deal?.property_info?.beds || 3) / units) : (deal?.property_info?.beds || 3);
    const bathsPerUnit = units > 1 ? Math.round(((deal?.property_info?.baths || 2) / units) * 10) / 10 : (deal?.property_info?.baths || 2);
    
    return (
      <Card className={`border-2 ${rentalAnalysis ? 'border-purple-300 bg-purple-50/30' : 'border-purple-200 bg-purple-50/20'}`}>
        <CardHeader className="pb-2 bg-purple-100/50 rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5 text-purple-500" />
            Rental Market Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {showButton && (
            <>
              {/* Property Type Selector */}
              <div className="mb-3">
                <Label className="text-sm text-slate-600 mb-2 block">Property Type</Label>
                <Select 
                  value={deal?.property_info?.property_type || "single_family"}
                  onValueChange={(value) => setter(prev => ({
                    ...prev,
                    property_info: { ...prev.property_info, property_type: value }
                  }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Unit Info for Multi-Family */}
              {units > 1 && (
                <div className="mb-3 p-2 bg-purple-100 rounded-lg text-xs text-purple-700">
                  <p><strong>Multi-unit detected:</strong> {units} units</p>
                  <p>Analyzing as {bedsPerUnit} bed / {bathsPerUnit} bath per unit</p>
                </div>
              )}
              
              {/* Radius Selector */}
              <div className="mb-3">
                <Label className="text-sm text-slate-600 mb-2 block">Search Radius</Label>
                <div className="flex gap-1">
                  {[0.5, 1, 2, 3, 5].map((radius) => (
                    <Button
                      key={radius}
                      variant={searchRadius === radius ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSearchRadius(radius)}
                      className={`flex-1 text-xs ${searchRadius === radius ? 'bg-purple-500 hover:bg-purple-400' : ''}`}
                    >
                      {radius} mi
                    </Button>
                  ))}
                </div>
              </div>
              
              <Button 
                onClick={fetchRentalAnalysis} 
                disabled={loadingRental} 
                className="w-full mb-4 bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3"
                size="lg"
              >
                {loadingRental ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Search className="h-5 w-5 mr-2" />
                )}
                {loadingRental ? "Analyzing..." : "Run Rental Analysis"}
              </Button>
            </>
          )}
          
          {rentalAnalysis ? (
            <div className="space-y-3">
              {/* Show per-unit and total for multi-family */}
              {units > 1 && (
                <div className="bg-purple-100 p-2 rounded-lg text-sm">
                  <p className="text-purple-700 font-medium">
                    Per Unit: {formatCurrency(rentalAnalysis.rent_estimate?.estimated_rent)} × {units} units = <strong>{formatCurrency((rentalAnalysis.rent_estimate?.estimated_rent || 0) * units)}/mo total</strong>
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white p-3 rounded-lg border border-purple-200">
                  <span className="text-xs text-slate-500 block">{units > 1 ? 'Rent Per Unit' : 'Estimated Rent'}</span>
                  <span className="font-mono font-bold text-lg text-purple-600">
                    {formatCurrency(rentalAnalysis.rent_estimate?.estimated_rent)}
                  </span>
                </div>
                <div className="bg-white p-3 rounded-lg border border-purple-200">
                  <span className="text-xs text-slate-500 block">Market Range</span>
                  <span className="font-mono text-sm">
                    {formatCurrency(rentalAnalysis.rent_estimate?.rent_low)} - {formatCurrency(rentalAnalysis.rent_estimate?.rent_high)}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Average:</span>
                  <span className="font-mono">{formatCurrency(rentalAnalysis.rent_estimate?.rent_average)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Median:</span>
                  <span className="font-mono">{formatCurrency(rentalAnalysis.rent_estimate?.rent_median)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Comps:</span>
                  <span>{rentalAnalysis.rent_estimate?.comps_count || rentalAnalysis.comparables?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Radius:</span>
                  <span>{rentalAnalysis.search_radius_miles || searchRadius} mi</span>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <Label className="text-sm">Include in exports</Label>
                <Switch checked={includeRentalAnalysis} onCheckedChange={setIncludeRentalAnalysis} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-2">
              Enter an address above and click "Run Rental Analysis" to get market rent data
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center space-x-2">
            <img src={LOGO_URL} alt="Rentlytix" className="h-12 w-auto" />
          </Link>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link to="/dashboard" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <BarChart3 className="h-5 w-5" /><span>Dashboard</span>
          </Link>
          <Link to="/reports" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <FileText className="h-5 w-5" /><span>My Reports</span>
          </Link>
          <Link to="/calculators" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white">
            <Calculator className="h-5 w-5" /><span>Deal Calculators</span>
          </Link>
          <Link to="/compare" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <TrendingUp className="h-5 w-5" /><span>Compare</span>
            {currentTier !== "pro" && currentTier !== "admin" && <Badge className="bg-purple-500 text-white text-xs ml-auto">PRO</Badge>}
          </Link>
          <Link to="/subscription" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <Crown className="h-5 w-5" /><span>Subscription</span>
          </Link>
          <Link to="/support" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <HelpCircle className="h-5 w-5" /><span>Support</span>
          </Link>
          <Link to="/settings" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
            <Settings className="h-5 w-5" /><span>Settings</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-teal-500 text-navy-900">{auth.user?.name?.charAt(0) || "U"}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                <Badge className={`${tierBadgeColor[currentTier]} text-xs`}>{currentTier.toUpperCase()}</Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10" onClick={auth.logout}>
              <LogOut className="h-4 w-4 mr-2" />Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-navy-900 text-white overflow-y-auto">
            <div className="p-4 flex items-center justify-between border-b border-white/10">
              <Link to="/" className="flex items-center space-x-2">
                <img src={LOGO_URL} alt="Rentlytix" className="h-10 w-auto" />
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="text-white hover:bg-white/10">
                <X className="h-6 w-6" />
              </Button>
            </div>
            
            <nav className="px-4 py-4 space-y-1">
              <Link to="/dashboard" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <BarChart3 className="h-5 w-5" /><span>Dashboard</span>
              </Link>
              <Link to="/reports" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <FileText className="h-5 w-5" /><span>My Reports</span>
              </Link>
              <Link to="/calculators" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white" onClick={() => setMobileMenuOpen(false)}>
                <Calculator className="h-5 w-5" /><span>Deal Calculators</span>
              </Link>
              <Link to="/compare" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <TrendingUp className="h-5 w-5" /><span>Compare</span>
                {currentTier !== "pro" && currentTier !== "admin" && <Badge className="bg-purple-500 text-white text-xs ml-auto">PRO</Badge>}
              </Link>
              <Link to="/subscription" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Crown className="h-5 w-5" /><span>Subscription</span>
              </Link>
              <Link to="/support" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <HelpCircle className="h-5 w-5" /><span>Support</span>
              </Link>
              <Link to="/settings" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Settings className="h-5 w-5" /><span>Settings</span>
              </Link>
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
              <div className="flex items-center space-x-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={auth.user?.picture} />
                  <AvatarFallback className="bg-teal-500 text-navy-900">{auth.user?.name?.charAt(0) || "U"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                  <Badge className={`${tierBadgeColor[currentTier]} text-xs`}>{currentTier.toUpperCase()}</Badge>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10" onClick={auth.logout}>
                <LogOut className="h-4 w-4 mr-2" />Sign Out
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)}>
              <Menu className="h-6 w-6" />
            </Button>
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Rentlytix" className="h-8 w-auto" />
            </Link>
          </div>
          <div className="flex items-center space-x-1">
            <Button variant="ghost" size="sm" onClick={exportToExcel} disabled={exportingExcel} className="text-xs">
              {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={exportToPDF} disabled={exportingPdf} className="text-xs">
              {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-6xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900">Deal Calculators</h1>
              <p className="text-slate-600">Analyze BRRRR, Fix & Flip, Buy & Hold, and Sub-To deals</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={exportToExcel} disabled={exportingExcel}>
                {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                Excel
              </Button>
              <Button onClick={exportToPDF} disabled={exportingPdf} className="bg-teal-500 hover:bg-teal-400 text-navy-900">
                {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                PDF
              </Button>
            </div>
          </div>

          {/* Zillow Import */}
          <Card className="mb-6 border-blue-200 bg-blue-50/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <LinkIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <Input
                  type="text"
                  placeholder="Paste Zillow listing URL to auto-fill property details..."
                  value={zillowUrl}
                  onChange={(e) => setZillowUrl(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleZillowImport} disabled={importingZillow} variant="outline" className="flex-shrink-0">
                  {importingZillow ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Calculator Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setRentalAnalysis(null); }}>
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="brrrr" className="data-[state=active]:bg-teal-500 data-[state=active]:text-navy-900">
                <RefreshCw className="h-4 w-4 mr-2" />BRRRR
              </TabsTrigger>
              <TabsTrigger value="flip" className="data-[state=active]:bg-teal-500 data-[state=active]:text-navy-900">
                <Hammer className="h-4 w-4 mr-2" />Flip
              </TabsTrigger>
              <TabsTrigger value="buyhold" className="data-[state=active]:bg-teal-500 data-[state=active]:text-navy-900">
                <Home className="h-4 w-4 mr-2" />Buy & Hold
              </TabsTrigger>
              <TabsTrigger value="subjectto" className="data-[state=active]:bg-teal-500 data-[state=active]:text-navy-900">
                <DollarSign className="h-4 w-4 mr-2" />Sub-To
              </TabsTrigger>
            </TabsList>

            {/* BRRRR Calculator */}
            <TabsContent value="brrrr">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <Section title="Property Info" icon={Building2}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label className="text-sm">Property Address</Label>
                        <div className="relative" ref={activeAddressField === 'brrrr' ? autocompleteRef : null}>
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                          <Input
                            type="text"
                            placeholder="Start typing an address..."
                            value={brrrrDeal.property_info.address}
                            onChange={(e) => handleAddressChange(e.target.value, setBrrrrDeal, 'brrrr')}
                            onFocus={() => { setActiveAddressField('brrrr'); suggestions.length > 0 && setShowSuggestions(true); }}
                            className="pl-10"
                            autoComplete="off"
                            data-testid="brrrr-address-input"
                          />
                          {loadingSuggestions && activeAddressField === 'brrrr' && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                          )}
                          
                          {/* Autocomplete Dropdown */}
                          {showSuggestions && suggestions.length > 0 && activeAddressField === 'brrrr' && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                              {suggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-0"
                                  onClick={() => selectSuggestion(suggestion, setBrrrrDeal)}
                                  data-testid={`brrrr-suggestion-${index}`}
                                >
                                  <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-medium text-slate-900 truncate">{suggestion.address}</p>
                                    <p className="text-sm text-slate-500 truncate">
                                      {suggestion.city}, {suggestion.state} {suggestion.zip_code}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Show auto-filled city/state/zip */}
                        {(brrrrDeal.property_info.city || brrrrDeal.property_info.state || brrrrDeal.property_info.zip_code) && (
                          <p className="text-xs text-slate-500 mt-1">
                            {[brrrrDeal.property_info.city, brrrrDeal.property_info.state, brrrrDeal.property_info.zip_code].filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                      <NumberInput label="Beds" value={brrrrDeal.property_info.beds} onChange={(v) => setBrrrrDeal(p => ({...p, property_info: {...p.property_info, beds: v}}))} />
                      <NumberInput label="Baths" value={brrrrDeal.property_info.baths} onChange={(v) => setBrrrrDeal(p => ({...p, property_info: {...p.property_info, baths: v}}))} />
                      <NumberInput label="Sqft" value={brrrrDeal.property_info.sqft} onChange={(v) => setBrrrrDeal(p => ({...p, property_info: {...p.property_info, sqft: v}}))} />
                      <NumberInput label="Units" value={brrrrDeal.property_info.units} onChange={(v) => setBrrrrDeal(p => ({...p, property_info: {...p.property_info, units: v}}))} />
                    </div>
                  </Section>
                  
                  <Section title="Acquisition" icon={DollarSign}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Purchase Price" prefix="$" value={brrrrDeal.acquisition.purchase_price} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "purchase_price", v)} />
                      <NumberInput label="Closing Costs %" suffix="%" value={brrrrDeal.acquisition.closing_costs_percent} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "closing_costs_percent", v)} />
                    </div>
                    
                    {/* Short Term Financing Toggle */}
                    <div className="mt-4 p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Label className="text-sm font-medium text-slate-700">Short Term Financing</Label>
                          <Switch 
                            checked={brrrrDeal.acquisition.short_term_io_enabled} 
                            onCheckedChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "short_term_io_enabled", v)}
                          />
                        </div>
                      </div>
                      
                      {brrrrDeal.acquisition.short_term_io_enabled ? (
                        <div className="grid grid-cols-2 gap-3">
                          <NumberInput label="Loan-to-Cost %" suffix="%" value={brrrrDeal.acquisition.short_term_io_ltc_percent} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "short_term_io_ltc_percent", v)} />
                          <NumberInput label="Interest Rate" suffix="%" value={brrrrDeal.acquisition.short_term_io_rate} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "short_term_io_rate", v)} />
                          <NumberInput label="Points %" suffix="%" value={brrrrDeal.acquisition.short_term_io_points} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "short_term_io_points", v)} />
                          <NumberInput label="Lender Fees" prefix="$" value={brrrrDeal.acquisition.lender_fees} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "lender_fees", v)} />
                          <div className="col-span-2 flex items-center gap-2">
                            <Switch 
                              id="brrrr-include-rehab"
                              checked={brrrrDeal.acquisition.short_term_io_include_rehab} 
                              onCheckedChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "short_term_io_include_rehab", v)}
                            />
                            <Label htmlFor="brrrr-include-rehab" className="text-xs text-slate-600">Include rehab in loan amount</Label>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <NumberInput label="Down Payment %" suffix="%" value={brrrrDeal.acquisition.down_payment_percent} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "down_payment_percent", v)} />
                          <NumberInput label="Interest Rate" suffix="%" value={brrrrDeal.acquisition.interest_rate} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "interest_rate", v)} />
                          <NumberInput label="Loan Term (years)" value={brrrrDeal.acquisition.amortization_years} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "amortization_years", v)} />
                          <NumberInput label="Points %" suffix="%" value={brrrrDeal.acquisition.points_percent} onChange={(v) => updateDeal(setBrrrrDeal)("acquisition", "points_percent", v)} />
                        </div>
                      )}
                    </div>
                    
                    <SectionMetrics items={[
                      ["Down Payment", formatCurrency(brrrrMetrics.down_payment)],
                      ["Loan Amount", formatCurrency(brrrrMetrics.loan_amount)],
                      ["Monthly P&I", formatCurrency(brrrrMetrics.initial_monthly_pi)],
                      ["Cash at Closing", formatCurrency(brrrrMetrics.total_cash_at_closing)],
                    ]} />
                  </Section>
                  
                  <Section title="Rehab & Holding Costs" icon={Hammer}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Rehab Budget" prefix="$" value={brrrrDeal.rehab.rehab_budget} onChange={(v) => updateDeal(setBrrrrDeal)("rehab", "rehab_budget", v)} />
                      <NumberInput label="Contingency %" suffix="%" value={brrrrDeal.rehab.contingency_percent} onChange={(v) => updateDeal(setBrrrrDeal)("rehab", "contingency_percent", v)} />
                      <NumberInput label="Timeline (months)" value={brrrrDeal.rehab.rehab_timeline_months} onChange={(v) => updateDeal(setBrrrrDeal)("rehab", "rehab_timeline_months", v)} />
                    </div>
                    
                    {/* Holding Costs During Rehab */}
                    <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">Monthly Holding Costs During Rehab</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <NumberInput label="Property Taxes/mo" prefix="$" value={brrrrDeal.rehab.holding_taxes_monthly} onChange={(v) => updateDeal(setBrrrrDeal)("rehab", "holding_taxes_monthly", v)} />
                        <NumberInput label="Insurance/mo" prefix="$" value={brrrrDeal.rehab.holding_insurance_monthly} onChange={(v) => updateDeal(setBrrrrDeal)("rehab", "holding_insurance_monthly", v)} />
                        <NumberInput label="Utilities/mo" prefix="$" value={brrrrDeal.rehab.holding_utilities_monthly} onChange={(v) => updateDeal(setBrrrrDeal)("rehab", "holding_utilities_monthly", v)} />
                        <NumberInput label="Other Holding/mo" prefix="$" value={brrrrDeal.rehab.holding_other_monthly} onChange={(v) => updateDeal(setBrrrrDeal)("rehab", "holding_other_monthly", v)} />
                      </div>
                    </div>
                    
                    <SectionMetrics items={[
                      ["Total Rehab Cost", formatCurrency(brrrrMetrics.total_rehab_cost)],
                      ["Holding Costs", formatCurrency(brrrrMetrics.holding_costs_total)],
                      ["Total Cash Before Refi", formatCurrency(brrrrMetrics.total_cash_before_refi)],
                    ]} />
                  </Section>
                  
                  <Section title="Rental Income & Expenses" icon={Home}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Monthly Rent" prefix="$" value={brrrrDeal.holding_rent.market_rent_monthly} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "market_rent_monthly", v)} />
                      <NumberInput label="Vacancy %" suffix="%" value={brrrrDeal.holding_rent.vacancy_percent} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "vacancy_percent", v)} />
                      <NumberInput label="Property Taxes/yr" prefix="$" value={brrrrDeal.holding_rent.property_taxes_annual} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "property_taxes_annual", v)} />
                      <NumberInput label="Insurance/yr" prefix="$" value={brrrrDeal.holding_rent.insurance_annual} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "insurance_annual", v)} />
                      <NumberInput label="Repairs %" suffix="%" value={brrrrDeal.holding_rent.repairs_percent} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "repairs_percent", v)} />
                      <NumberInput label="CapEx %" suffix="%" value={brrrrDeal.holding_rent.capex_percent} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "capex_percent", v)} />
                      <NumberInput label="Property Mgmt %" suffix="%" value={brrrrDeal.holding_rent.property_management_percent} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "property_management_percent", v)} />
                      <NumberInput label="HOA/mo" prefix="$" value={brrrrDeal.holding_rent.hoa_monthly} onChange={(v) => updateDeal(setBrrrrDeal)("holding_rent", "hoa_monthly", v)} />
                    </div>
                    
                    <SectionMetrics items={[
                      ["Gross Rent", formatCurrency(brrrrMetrics.gross_scheduled_rent)],
                      ["Effective Income", formatCurrency(brrrrMetrics.effective_gross_income)],
                      ["Total Expenses", formatCurrency(brrrrMetrics.total_operating_expenses)],
                      ["NOI", formatCurrency(brrrrMetrics.noi)],
                    ]} />
                  </Section>
                  
                  <Section title="Refinance" icon={RefreshCw}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="ARV (After Repair Value)" prefix="$" value={brrrrDeal.refinance.arv} onChange={(v) => updateDeal(setBrrrrDeal)("refinance", "arv", v)} />
                      <NumberInput label="Refi LTV %" suffix="%" value={brrrrDeal.refinance.refi_ltv_percent} onChange={(v) => updateDeal(setBrrrrDeal)("refinance", "refi_ltv_percent", v)} />
                      <NumberInput label="New Interest Rate" suffix="%" value={brrrrDeal.refinance.new_interest_rate} onChange={(v) => updateDeal(setBrrrrDeal)("refinance", "new_interest_rate", v)} />
                      <NumberInput label="New Loan Term (years)" value={brrrrDeal.refinance.new_amortization_years} onChange={(v) => updateDeal(setBrrrrDeal)("refinance", "new_amortization_years", v)} />
                    </div>
                    
                    <SectionMetrics items={[
                      ["New Loan Amount", formatCurrency(brrrrMetrics.new_loan_amount)],
                      ["New Monthly P&I", formatCurrency(brrrrMetrics.new_monthly_pi)],
                      ["Cash Out from Refi", formatCurrency(brrrrMetrics.cash_out_from_refi)],
                      ["Cash Left in Deal", formatCurrency(brrrrMetrics.cash_left_in_deal)],
                    ]} />
                  </Section>
                </div>
                
                {/* Results Panel */}
                <div className="space-y-4">
                  {/* Rental Analysis First - Always Visible */}
                  <RentalAnalysisCard />
                  
                  <Card>
                    <CardHeader className="pb-2 bg-slate-50 rounded-t-lg">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-teal-500" />
                        BRRRR Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-4">
                      <MetricCard label="Total Cash Before Refi" value={brrrrMetrics.total_cash_before_refi} />
                      <MetricCard label="Cash Out from Refi" value={brrrrMetrics.cash_out_from_refi} highlight={brrrrMetrics.cash_out_from_refi > 0 ? "positive" : "negative"} />
                      <MetricCard label="Cash Left in Deal" value={brrrrMetrics.cash_left_in_deal} highlight={brrrrMetrics.cash_left_in_deal <= 0 ? "positive" : undefined} />
                      <Separator />
                      <MetricCard label="Monthly Cash Flow" value={brrrrMetrics.monthly_cash_flow} highlight={brrrrMetrics.monthly_cash_flow > 0 ? "positive" : "negative"} />
                      <MetricCard label="Cash-on-Cash Return" value={brrrrMetrics.cash_on_cash_return} isPercent isCurrency={false} highlight={brrrrMetrics.cash_on_cash_return > 10 ? "positive" : undefined} />
                      <MetricCard label="NOI" value={brrrrMetrics.noi} />
                      <MetricCard label="Cap Rate (ARV)" value={brrrrMetrics.cap_rate_arv} isPercent isCurrency={false} />
                      <MetricCard label="DSCR" value={brrrrMetrics.dscr} isCurrency={false} highlight={brrrrMetrics.dscr >= 1.25 ? "positive" : brrrrMetrics.dscr < 1 ? "negative" : undefined} />
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Flip Calculator */}
            <TabsContent value="flip">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <Section title="Property Info" icon={Building2}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label className="text-sm">Property Address</Label>
                        <div className="relative" ref={activeAddressField === 'flip' ? autocompleteRef : null}>
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                          <Input
                            type="text"
                            placeholder="Start typing an address..."
                            value={flipDeal.property_info.address}
                            onChange={(e) => handleAddressChange(e.target.value, setFlipDeal, 'flip')}
                            onFocus={() => { setActiveAddressField('flip'); suggestions.length > 0 && setShowSuggestions(true); }}
                            className="pl-10"
                            autoComplete="off"
                            data-testid="flip-address-input"
                          />
                          {loadingSuggestions && activeAddressField === 'flip' && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                          )}
                          
                          {/* Autocomplete Dropdown */}
                          {showSuggestions && suggestions.length > 0 && activeAddressField === 'flip' && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                              {suggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-0"
                                  onClick={() => selectSuggestion(suggestion, setFlipDeal)}
                                  data-testid={`flip-suggestion-${index}`}
                                >
                                  <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-medium text-slate-900 truncate">{suggestion.address}</p>
                                    <p className="text-sm text-slate-500 truncate">
                                      {suggestion.city}, {suggestion.state} {suggestion.zip_code}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Show auto-filled city/state/zip */}
                        {(flipDeal.property_info.city || flipDeal.property_info.state || flipDeal.property_info.zip_code) && (
                          <p className="text-xs text-slate-500 mt-1">
                            {[flipDeal.property_info.city, flipDeal.property_info.state, flipDeal.property_info.zip_code].filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                      <NumberInput label="Beds" value={flipDeal.property_info.beds} onChange={(v) => setFlipDeal(p => ({...p, property_info: {...p.property_info, beds: v}}))} />
                      <NumberInput label="Baths" value={flipDeal.property_info.baths} onChange={(v) => setFlipDeal(p => ({...p, property_info: {...p.property_info, baths: v}}))} />
                    </div>
                  </Section>
                  
                  <Section title="Acquisition" icon={DollarSign}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Purchase Price" prefix="$" value={flipDeal.acquisition.purchase_price} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "purchase_price", v)} />
                      <NumberInput label="Closing Costs %" suffix="%" value={flipDeal.acquisition.closing_costs_percent} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "closing_costs_percent", v)} />
                    </div>
                    
                    {/* Short Term Financing Toggle */}
                    <div className="mt-4 p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Label className="text-sm font-medium text-slate-700">Short Term Financing</Label>
                          <Switch 
                            checked={flipDeal.acquisition.short_term_io_enabled} 
                            onCheckedChange={(v) => updateDeal(setFlipDeal)("acquisition", "short_term_io_enabled", v)}
                          />
                        </div>
                      </div>
                      
                      {flipDeal.acquisition.short_term_io_enabled ? (
                        <div className="grid grid-cols-2 gap-3">
                          <NumberInput label="Loan-to-Cost %" suffix="%" value={flipDeal.acquisition.short_term_io_ltc_percent} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "short_term_io_ltc_percent", v)} />
                          <NumberInput label="Interest Rate" suffix="%" value={flipDeal.acquisition.short_term_io_rate} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "short_term_io_rate", v)} />
                          <NumberInput label="Points %" suffix="%" value={flipDeal.acquisition.short_term_io_points} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "short_term_io_points", v)} />
                          <NumberInput label="Lender Fees" prefix="$" value={flipDeal.acquisition.lender_fees} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "lender_fees", v)} />
                          <div className="col-span-2 flex items-center gap-2">
                            <Switch 
                              id="flip-include-rehab"
                              checked={flipDeal.acquisition.short_term_io_include_rehab} 
                              onCheckedChange={(v) => updateDeal(setFlipDeal)("acquisition", "short_term_io_include_rehab", v)}
                            />
                            <Label htmlFor="flip-include-rehab" className="text-xs text-slate-600">Include rehab in loan amount</Label>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <NumberInput label="Down Payment %" suffix="%" value={flipDeal.acquisition.down_payment_percent} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "down_payment_percent", v)} />
                          <NumberInput label="Interest Rate" suffix="%" value={flipDeal.acquisition.interest_rate} onChange={(v) => updateDeal(setFlipDeal)("acquisition", "interest_rate", v)} />
                        </div>
                      )}
                    </div>
                    
                    <SectionMetrics items={[
                      ["Down Payment", formatCurrency(flipMetrics.down_payment)],
                      ["Loan Amount", formatCurrency(flipMetrics.loan_amount)],
                      ["Monthly Payment", formatCurrency(flipMetrics.initial_monthly_payment)],
                      ["Cash at Closing", formatCurrency(flipMetrics.total_cash_at_closing)],
                    ]} />
                  </Section>
                  
                  <Section title="Rehab & Holding Costs" icon={Hammer}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Rehab Budget" prefix="$" value={flipDeal.rehab.rehab_budget} onChange={(v) => updateDeal(setFlipDeal)("rehab", "rehab_budget", v)} />
                      <NumberInput label="Contingency %" suffix="%" value={flipDeal.rehab.contingency_percent} onChange={(v) => updateDeal(setFlipDeal)("rehab", "contingency_percent", v)} />
                      <NumberInput label="Timeline (months)" value={flipDeal.rehab.rehab_timeline_months} onChange={(v) => updateDeal(setFlipDeal)("rehab", "rehab_timeline_months", v)} />
                    </div>
                    
                    {/* Holding Costs During Rehab */}
                    <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">Monthly Holding Costs During Rehab</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <NumberInput label="Property Taxes/mo" prefix="$" value={flipDeal.rehab.holding_taxes_monthly} onChange={(v) => updateDeal(setFlipDeal)("rehab", "holding_taxes_monthly", v)} />
                        <NumberInput label="Insurance/mo" prefix="$" value={flipDeal.rehab.holding_insurance_monthly} onChange={(v) => updateDeal(setFlipDeal)("rehab", "holding_insurance_monthly", v)} />
                        <NumberInput label="Utilities/mo" prefix="$" value={flipDeal.rehab.holding_utilities_monthly} onChange={(v) => updateDeal(setFlipDeal)("rehab", "holding_utilities_monthly", v)} />
                        <NumberInput label="Other Holding/mo" prefix="$" value={flipDeal.rehab.holding_other_monthly} onChange={(v) => updateDeal(setFlipDeal)("rehab", "holding_other_monthly", v)} />
                      </div>
                    </div>
                    
                    <SectionMetrics items={[
                      ["Total Rehab Cost", formatCurrency(flipMetrics.total_rehab_cost)],
                      ["Holding Costs", formatCurrency(flipMetrics.holding_costs_total)],
                      ["Total Cash Invested", formatCurrency(flipMetrics.total_cash_invested)],
                    ]} />
                  </Section>
                  
                  <Section title="Sale" icon={TrendingUp}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="ARV / Sale Price" prefix="$" value={flipDeal.sale.arv} onChange={(v) => updateDeal(setFlipDeal)("sale", "arv", v)} />
                      <NumberInput label="Selling Costs %" suffix="%" value={flipDeal.sale.selling_costs_percent} onChange={(v) => updateDeal(setFlipDeal)("sale", "selling_costs_percent", v)} />
                      <NumberInput label="Holding Time (months)" value={flipDeal.sale.holding_time_months} onChange={(v) => updateDeal(setFlipDeal)("sale", "holding_time_months", v)} />
                    </div>
                    
                    <SectionMetrics items={[
                      ["Gross Sale Price", formatCurrency(flipMetrics.gross_sale_price)],
                      ["Selling Costs", formatCurrency(flipMetrics.selling_costs)],
                      ["Net Sale Proceeds", formatCurrency(flipMetrics.net_sale_proceeds)],
                      ["Net Profit", formatCurrency(flipMetrics.net_profit)],
                    ]} />
                  </Section>
                </div>
                
                {/* Results */}
                <div>
                  <Card className="sticky top-4">
                    <CardHeader className="pb-2 bg-slate-50 rounded-t-lg">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Hammer className="h-5 w-5 text-orange-500" />
                        Flip Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-4">
                      <MetricCard label="Total Cash Invested" value={flipMetrics.total_cash_invested} />
                      <MetricCard label="Total Project Cost" value={flipMetrics.total_project_cost} />
                      <Separator />
                      <MetricCard label="Gross Sale Price" value={flipMetrics.gross_sale_price} />
                      <MetricCard label="Selling Costs" value={flipMetrics.selling_costs} />
                      <MetricCard label="Net Profit" value={flipMetrics.net_profit} highlight={flipMetrics.net_profit > 0 ? "positive" : "negative"} />
                      <Separator />
                      <MetricCard label="ROI" value={flipMetrics.roi_percent} isPercent isCurrency={false} highlight={flipMetrics.roi_percent > 20 ? "positive" : undefined} />
                      <MetricCard label="Annualized ROI" value={flipMetrics.annualized_roi} isPercent isCurrency={false} />
                      <MetricCard label="Profit Margin" value={flipMetrics.profit_margin} isPercent isCurrency={false} />
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Buy & Hold Calculator */}
            <TabsContent value="buyhold">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <Section title="Property Info" icon={Building2}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label className="text-sm">Property Address</Label>
                        <div className="relative" ref={activeAddressField === 'buyhold' ? autocompleteRef : null}>
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                          <Input
                            type="text"
                            placeholder="Start typing an address..."
                            value={buyHoldDeal.property_info.address}
                            onChange={(e) => handleAddressChange(e.target.value, setBuyHoldDeal, 'buyhold')}
                            onFocus={() => { setActiveAddressField('buyhold'); suggestions.length > 0 && setShowSuggestions(true); }}
                            className="pl-10"
                            autoComplete="off"
                            data-testid="buyhold-address-input"
                          />
                          {loadingSuggestions && activeAddressField === 'buyhold' && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                          )}
                          
                          {/* Autocomplete Dropdown */}
                          {showSuggestions && suggestions.length > 0 && activeAddressField === 'buyhold' && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                              {suggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-0"
                                  onClick={() => selectSuggestion(suggestion, setBuyHoldDeal)}
                                  data-testid={`buyhold-suggestion-${index}`}
                                >
                                  <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-medium text-slate-900 truncate">{suggestion.address}</p>
                                    <p className="text-sm text-slate-500 truncate">
                                      {suggestion.city}, {suggestion.state} {suggestion.zip_code}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Show auto-filled city/state/zip */}
                        {(buyHoldDeal.property_info.city || buyHoldDeal.property_info.state || buyHoldDeal.property_info.zip_code) && (
                          <p className="text-xs text-slate-500 mt-1">
                            {[buyHoldDeal.property_info.city, buyHoldDeal.property_info.state, buyHoldDeal.property_info.zip_code].filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                      <NumberInput label="Beds" value={buyHoldDeal.property_info.beds} onChange={(v) => setBuyHoldDeal(p => ({...p, property_info: {...p.property_info, beds: v}}))} />
                      <NumberInput label="Baths" value={buyHoldDeal.property_info.baths} onChange={(v) => setBuyHoldDeal(p => ({...p, property_info: {...p.property_info, baths: v}}))} />
                      <NumberInput label="Sqft" value={buyHoldDeal.property_info.sqft} onChange={(v) => setBuyHoldDeal(p => ({...p, property_info: {...p.property_info, sqft: v}}))} />
                      <NumberInput label="Units" value={buyHoldDeal.property_info.units} onChange={(v) => setBuyHoldDeal(p => ({...p, property_info: {...p.property_info, units: v}}))} />
                    </div>
                  </Section>
                  
                  <Section title="Acquisition" icon={DollarSign}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Purchase Price" prefix="$" value={buyHoldDeal.acquisition.purchase_price} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "purchase_price", v)} />
                      <NumberInput label="Closing Costs %" suffix="%" value={buyHoldDeal.acquisition.closing_costs_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "closing_costs_percent", v)} />
                    </div>
                    
                    {/* Short Term Financing Toggle */}
                    <div className="mt-4 p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Label className="text-sm font-medium text-slate-700">Short Term Financing</Label>
                          <Switch 
                            checked={buyHoldDeal.acquisition.short_term_io_enabled} 
                            onCheckedChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "short_term_io_enabled", v)}
                          />
                        </div>
                      </div>
                      
                      {buyHoldDeal.acquisition.short_term_io_enabled ? (
                        <div className="grid grid-cols-2 gap-3">
                          <NumberInput label="Loan-to-Cost %" suffix="%" value={buyHoldDeal.acquisition.short_term_io_ltc_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "short_term_io_ltc_percent", v)} />
                          <NumberInput label="Interest Rate" suffix="%" value={buyHoldDeal.acquisition.short_term_io_rate} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "short_term_io_rate", v)} />
                          <NumberInput label="Points %" suffix="%" value={buyHoldDeal.acquisition.short_term_io_points} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "short_term_io_points", v)} />
                          <NumberInput label="Lender Fees" prefix="$" value={buyHoldDeal.acquisition.lender_fees} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "lender_fees", v)} />
                          <div className="col-span-2 flex items-center gap-2">
                            <Switch 
                              id="buyhold-include-rehab"
                              checked={buyHoldDeal.acquisition.short_term_io_include_rehab} 
                              onCheckedChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "short_term_io_include_rehab", v)}
                            />
                            <Label htmlFor="buyhold-include-rehab" className="text-xs text-slate-600">Include rehab in loan amount</Label>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <NumberInput label="Down Payment %" suffix="%" value={buyHoldDeal.acquisition.down_payment_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "down_payment_percent", v)} />
                          <NumberInput label="Interest Rate" suffix="%" value={buyHoldDeal.acquisition.interest_rate} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "interest_rate", v)} />
                          <NumberInput label="Loan Term (years)" value={buyHoldDeal.acquisition.amortization_years} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "amortization_years", v)} />
                          <NumberInput label="Points %" suffix="%" value={buyHoldDeal.acquisition.points_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("acquisition", "points_percent", v)} />
                        </div>
                      )}
                    </div>
                    
                    <SectionMetrics items={[
                      ["Down Payment", formatCurrency(buyHoldMetrics.down_payment)],
                      ["Loan Amount", formatCurrency(buyHoldMetrics.loan_amount)],
                      ["Monthly P&I", formatCurrency(buyHoldMetrics.monthly_pi)],
                      ["Cash at Closing", formatCurrency(buyHoldMetrics.total_cash_at_closing)],
                    ]} />
                  </Section>
                  
                  <Section title="Rehab & Holding Costs (Optional)" icon={Hammer} defaultOpen={false}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Rehab Budget" prefix="$" value={buyHoldDeal.rehab.rehab_budget} onChange={(v) => updateDeal(setBuyHoldDeal)("rehab", "rehab_budget", v)} />
                      <NumberInput label="Contingency %" suffix="%" value={buyHoldDeal.rehab.contingency_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("rehab", "contingency_percent", v)} />
                      <NumberInput label="Timeline (months)" value={buyHoldDeal.rehab.rehab_timeline_months} onChange={(v) => updateDeal(setBuyHoldDeal)("rehab", "rehab_timeline_months", v)} />
                    </div>
                    
                    {/* Holding Costs During Rehab */}
                    <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">Monthly Holding Costs During Rehab</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <NumberInput label="Property Taxes/mo" prefix="$" value={buyHoldDeal.rehab.holding_taxes_monthly} onChange={(v) => updateDeal(setBuyHoldDeal)("rehab", "holding_taxes_monthly", v)} />
                        <NumberInput label="Insurance/mo" prefix="$" value={buyHoldDeal.rehab.holding_insurance_monthly} onChange={(v) => updateDeal(setBuyHoldDeal)("rehab", "holding_insurance_monthly", v)} />
                        <NumberInput label="Utilities/mo" prefix="$" value={buyHoldDeal.rehab.holding_utilities_monthly} onChange={(v) => updateDeal(setBuyHoldDeal)("rehab", "holding_utilities_monthly", v)} />
                        <NumberInput label="Other Holding/mo" prefix="$" value={buyHoldDeal.rehab.holding_other_monthly} onChange={(v) => updateDeal(setBuyHoldDeal)("rehab", "holding_other_monthly", v)} />
                      </div>
                    </div>
                    
                    <SectionMetrics items={[
                      ["Total Rehab Cost", formatCurrency(buyHoldMetrics.total_rehab_cost)],
                      ["Total Cash Invested", formatCurrency(buyHoldMetrics.total_cash_invested)],
                    ]} />
                  </Section>
                  
                  <Section title="Rental Income & Expenses" icon={Home}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Monthly Rent" prefix="$" value={buyHoldDeal.holding_rent.market_rent_monthly} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "market_rent_monthly", v)} />
                      <NumberInput label="Vacancy %" suffix="%" value={buyHoldDeal.holding_rent.vacancy_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "vacancy_percent", v)} />
                      <NumberInput label="Property Taxes/yr" prefix="$" value={buyHoldDeal.holding_rent.property_taxes_annual} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "property_taxes_annual", v)} />
                      <NumberInput label="Insurance/yr" prefix="$" value={buyHoldDeal.holding_rent.insurance_annual} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "insurance_annual", v)} />
                      <NumberInput label="Repairs %" suffix="%" value={buyHoldDeal.holding_rent.repairs_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "repairs_percent", v)} />
                      <NumberInput label="CapEx %" suffix="%" value={buyHoldDeal.holding_rent.capex_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "capex_percent", v)} />
                      <NumberInput label="Property Mgmt %" suffix="%" value={buyHoldDeal.holding_rent.property_management_percent} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "property_management_percent", v)} />
                      <NumberInput label="HOA/mo" prefix="$" value={buyHoldDeal.holding_rent.hoa_monthly} onChange={(v) => updateDeal(setBuyHoldDeal)("holding_rent", "hoa_monthly", v)} />
                    </div>
                    
                    <SectionMetrics items={[
                      ["Gross Rent (Annual)", formatCurrency(buyHoldMetrics.gross_scheduled_rent)],
                      ["Effective Income", formatCurrency(buyHoldMetrics.effective_gross_income)],
                      ["Total Expenses", formatCurrency(buyHoldMetrics.total_operating_expenses)],
                      ["NOI", formatCurrency(buyHoldMetrics.noi)],
                    ]} />
                  </Section>
                </div>
                
                {/* Results */}
                <div className="space-y-4">
                  {/* Rental Analysis First - Always Visible */}
                  <RentalAnalysisCard />
                  
                  <Card>
                    <CardHeader className="pb-2 bg-slate-50 rounded-t-lg">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Home className="h-5 w-5 text-blue-500" />
                        Buy & Hold Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-4">
                      <MetricCard label="Total Cash Invested" value={buyHoldMetrics.total_cash_invested} />
                      <MetricCard label="Total Property Value" value={buyHoldMetrics.total_property_value} />
                      <MetricCard label="Equity" value={buyHoldMetrics.equity} />
                      <Separator />
                      <MetricCard label="Monthly Cash Flow" value={buyHoldMetrics.monthly_cash_flow} highlight={buyHoldMetrics.monthly_cash_flow > 0 ? "positive" : "negative"} />
                      <MetricCard label="Annual Cash Flow" value={buyHoldMetrics.annual_cash_flow} />
                      <MetricCard label="Cash-on-Cash Return" value={buyHoldMetrics.cash_on_cash_return} isPercent isCurrency={false} highlight={buyHoldMetrics.cash_on_cash_return > 8 ? "positive" : undefined} />
                      <Separator />
                      <MetricCard label="NOI" value={buyHoldMetrics.noi} />
                      <MetricCard label="Cap Rate" value={buyHoldMetrics.cap_rate} isPercent isCurrency={false} />
                      <MetricCard label="DSCR" value={buyHoldMetrics.dscr} isCurrency={false} highlight={buyHoldMetrics.dscr >= 1.25 ? "positive" : buyHoldMetrics.dscr < 1 ? "negative" : undefined} />
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Subject-To + Seller Finance Calculator */}
            <TabsContent value="subjectto">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  
                  {/* Property Info */}
                  <Section title="Property Info" icon={Building2}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label className="text-sm">Property Address</Label>
                        <div className="relative" ref={activeAddressField === 'subjectto' ? autocompleteRef : null}>
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                          <Input
                            type="text"
                            placeholder="Start typing an address..."
                            value={subjectToDeal.property_info.address}
                            onChange={(e) => handleAddressChange(e.target.value, setSubjectToDeal, 'subjectto')}
                            onFocus={() => { setActiveAddressField('subjectto'); suggestions.length > 0 && setShowSuggestions(true); }}
                            className="pl-10"
                            autoComplete="off"
                            data-testid="subjectto-address-input"
                          />
                          {loadingSuggestions && activeAddressField === 'subjectto' && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                          )}
                          {showSuggestions && suggestions.length > 0 && activeAddressField === 'subjectto' && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                              {suggestions.map((suggestion, index) => (
                                <button key={index} type="button" className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-0"
                                  onClick={() => selectSuggestion(suggestion, setSubjectToDeal)}>
                                  <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-medium text-slate-900 truncate">{suggestion.address}</p>
                                    <p className="text-sm text-slate-500 truncate">{suggestion.city}, {suggestion.state} {suggestion.zip_code}</p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {(subjectToDeal.property_info.city || subjectToDeal.property_info.state || subjectToDeal.property_info.zip_code) && (
                          <p className="text-xs text-slate-500 mt-1">{[subjectToDeal.property_info.city, subjectToDeal.property_info.state, subjectToDeal.property_info.zip_code].filter(Boolean).join(", ")}</p>
                        )}
                      </div>
                      
                      {/* Units */}
                      <NumberInput label="Units" value={subjectToDeal.property_info.units} onChange={(v) => {
                        const units = Math.max(1, v);
                        setSubjectToDeal(p => ({
                          ...p, 
                          property_info: {
                            ...p.property_info, 
                            units: units,
                            beds: units * (p.property_info.beds_per_unit || 3),
                            baths: units * (p.property_info.baths_per_unit || 2)
                          }
                        }));
                      }} />
                      
                      {/* Beds/Baths per unit for multi-unit */}
                      {subjectToDeal.property_info.units > 1 ? (
                        <>
                          <NumberInput label="Beds/Unit" value={subjectToDeal.property_info.beds_per_unit || 3} onChange={(v) => {
                            setSubjectToDeal(p => ({
                              ...p, 
                              property_info: {
                                ...p.property_info, 
                                beds_per_unit: v,
                                beds: p.property_info.units * v
                              }
                            }));
                          }} />
                          <NumberInput label="Baths/Unit" value={subjectToDeal.property_info.baths_per_unit || 2} onChange={(v) => {
                            setSubjectToDeal(p => ({
                              ...p, 
                              property_info: {
                                ...p.property_info, 
                                baths_per_unit: v,
                                baths: p.property_info.units * v
                              }
                            }));
                          }} />
                          <div className="col-span-2 text-xs text-slate-500 bg-slate-50 p-2 rounded">
                            Total: {subjectToDeal.property_info.beds} beds, {subjectToDeal.property_info.baths} baths ({subjectToDeal.property_info.units} units)
                          </div>
                        </>
                      ) : (
                        <>
                          <NumberInput label="Beds" value={subjectToDeal.property_info.beds} onChange={(v) => setSubjectToDeal(p => ({...p, property_info: {...p.property_info, beds: v}}))} />
                          <NumberInput label="Baths" value={subjectToDeal.property_info.baths} onChange={(v) => setSubjectToDeal(p => ({...p, property_info: {...p.property_info, baths: v}}))} />
                        </>
                      )}
                      <NumberInput label="Sqft" value={subjectToDeal.property_info.sqft} onChange={(v) => setSubjectToDeal(p => ({...p, property_info: {...p.property_info, sqft: v}}))} />
                    </div>
                  </Section>
                  
                  {/* Rental Income & Expenses - Matching Buy & Hold */}
                  <Section title="Rental Income & Expenses" icon={Home}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Monthly Rent" prefix="$" value={subjectToDeal.income.monthly_rent} onChange={(v) => updateDeal(setSubjectToDeal)("income", "monthly_rent", v)} />
                      <NumberInput label="Vacancy %" suffix="%" value={subjectToDeal.reserves.vacancy_percent} onChange={(v) => updateDeal(setSubjectToDeal)("reserves", "vacancy_percent", v)} />
                      <NumberInput label="Property Taxes (monthly)" prefix="$" value={subjectToDeal.expenses.property_taxes_monthly} onChange={(v) => updateDeal(setSubjectToDeal)("expenses", "property_taxes_monthly", v)} />
                      <NumberInput label="Insurance (monthly)" prefix="$" value={subjectToDeal.expenses.insurance_monthly} onChange={(v) => updateDeal(setSubjectToDeal)("expenses", "insurance_monthly", v)} />
                      <NumberInput label="Repairs %" suffix="%" value={subjectToDeal.reserves.repairs_percent} onChange={(v) => updateDeal(setSubjectToDeal)("reserves", "repairs_percent", v)} />
                      <NumberInput label="CapEx %" suffix="%" value={subjectToDeal.reserves.capex_percent} onChange={(v) => updateDeal(setSubjectToDeal)("reserves", "capex_percent", v)} />
                      <NumberInput label="Property Mgmt %" suffix="%" value={subjectToDeal.reserves.property_management_percent} onChange={(v) => updateDeal(setSubjectToDeal)("reserves", "property_management_percent", v)} />
                      <NumberInput label="HOA (monthly)" prefix="$" value={subjectToDeal.expenses.hoa_monthly} onChange={(v) => updateDeal(setSubjectToDeal)("expenses", "hoa_monthly", v)} />
                      <NumberInput label="Utilities (monthly)" prefix="$" value={subjectToDeal.expenses.utilities_monthly} onChange={(v) => updateDeal(setSubjectToDeal)("expenses", "utilities_monthly", v)} />
                      <NumberInput label="Other (monthly)" prefix="$" value={subjectToDeal.expenses.other_fixed_monthly} onChange={(v) => updateDeal(setSubjectToDeal)("expenses", "other_fixed_monthly", v)} />
                    </div>
                    
                    <SectionMetrics items={[
                      ["Gross Rent (Annual)", formatCurrency(subjectToMetrics.gross_scheduled_rent * 12)],
                      ["Effective Income", formatCurrency(subjectToMetrics.effective_gross_income)],
                      ["Total Expenses", formatCurrency(subjectToMetrics.total_operating_expenses)],
                      ["NOI", formatCurrency(subjectToMetrics.noi)],
                    ]} />
                  </Section>
                  
                  {/* Acquisition & Subject-To Mortgage */}
                  <Section title="Acquisition & Subject-To Mortgage" icon={DollarSign}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Purchase Price" prefix="$" value={subjectToDeal.subject_to.purchase_price} onChange={(v) => updateDeal(setSubjectToDeal)("subject_to", "purchase_price", v)} />
                      <NumberInput label="Closing Costs %" suffix="%" value={subjectToDeal.subject_to.closing_costs_percent} onChange={(v) => updateDeal(setSubjectToDeal)("subject_to", "closing_costs_percent", v)} />
                    </div>
                    
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <Label className="text-sm font-semibold text-blue-800 mb-3 block">Existing Loan (Taking Subject-To)</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <NumberInput label="Existing Loan Balance" prefix="$" value={subjectToDeal.subject_to.existing_loan_balance} onChange={(v) => updateDeal(setSubjectToDeal)("subject_to", "existing_loan_balance", v)} />
                        <NumberInput label="Interest Rate" suffix="%" value={subjectToDeal.subject_to.existing_interest_rate} onChange={(v) => updateDeal(setSubjectToDeal)("subject_to", "existing_interest_rate", v)} />
                        <NumberInput label="Remaining Term (years)" value={subjectToDeal.subject_to.existing_remaining_years} onChange={(v) => updateDeal(setSubjectToDeal)("subject_to", "existing_remaining_years", v)} />
                        <NumberInput label="Monthly Payment (override)" prefix="$" value={subjectToDeal.subject_to.existing_monthly_payment} onChange={(v) => updateDeal(setSubjectToDeal)("subject_to", "existing_monthly_payment", v)} />
                      </div>
                      <p className="text-xs text-blue-600 mt-2">Leave monthly payment at $0 to auto-calculate based on balance, rate, and term</p>
                    </div>
                    
                    <SectionMetrics items={[
                      ["Subject-To Payment", formatCurrency(subjectToMetrics.subject_to_payment)],
                      ["Closing Costs", formatCurrency(subjectToMetrics.closing_costs)],
                    ]} />
                  </Section>
                  
                  {/* Seller Finance Note */}
                  <Section title="Seller Finance Note" icon={FileText}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Down Payment (Cash to Seller)" prefix="$" value={subjectToDeal.seller_finance.down_payment} onChange={(v) => updateDeal(setSubjectToDeal)("seller_finance", "down_payment", v)} />
                      <div>
                        <Label className="text-sm mb-1 block">Seller Finance Amount</Label>
                        <div className="text-lg font-mono font-semibold text-teal-600">{formatCurrency(subjectToMetrics.seller_finance_amount)}</div>
                        <p className="text-xs text-slate-500">Auto: Purchase - Existing Loan - Down Payment</p>
                      </div>
                      <NumberInput label="Seller Interest Rate" suffix="%" value={subjectToDeal.seller_finance.interest_rate} onChange={(v) => updateDeal(setSubjectToDeal)("seller_finance", "interest_rate", v)} />
                      <NumberInput label="Amortization (years)" value={subjectToDeal.seller_finance.amortization_years} onChange={(v) => updateDeal(setSubjectToDeal)("seller_finance", "amortization_years", v)} />
                    </div>
                    
                    <SectionMetrics items={[
                      ["Seller Finance Amount", formatCurrency(subjectToMetrics.seller_finance_amount)],
                      ["Seller Finance Payment", formatCurrency(subjectToMetrics.seller_finance_payment)],
                      ["Total Monthly Debt Service", formatCurrency(subjectToMetrics.total_monthly_debt_service)],
                    ]} />
                  </Section>
                  
                  {/* Initial Cash Invested */}
                  <Section title="Initial Cash Invested" icon={Wallet}>
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput label="Down Payment" prefix="$" value={subjectToDeal.seller_finance.down_payment} onChange={(v) => updateDeal(setSubjectToDeal)("seller_finance", "down_payment", v)} />
                      <div>
                        <Label className="text-sm mb-1 block">Closing Costs</Label>
                        <div className="text-base font-mono text-slate-700">{formatCurrency(subjectToMetrics.closing_costs)}</div>
                      </div>
                      <NumberInput label="Initial Repairs/Turn Costs" prefix="$" value={subjectToDeal.cash_invested.initial_repairs} onChange={(v) => updateDeal(setSubjectToDeal)("cash_invested", "initial_repairs", v)} />
                      <NumberInput label="Other Upfront Costs" prefix="$" value={subjectToDeal.cash_invested.other_upfront} onChange={(v) => updateDeal(setSubjectToDeal)("cash_invested", "other_upfront", v)} />
                    </div>
                    
                    <SectionMetrics items={[
                      ["Total Cash Invested", formatCurrency(subjectToMetrics.total_cash_invested)],
                    ]} />
                  </Section>
                </div>
                
                {/* Results Panel */}
                <div className="space-y-4">
                  {/* Rental Analysis First - Always Visible (matching Buy & Hold) */}
                  <RentalAnalysisCard />
                  
                  {/* Deal Quality Indicator */}
                  <Card className={`border-2 ${subjectToMetrics.is_deal ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'}`}>
                    <CardContent className="pt-6 text-center">
                      <div className={`text-4xl font-bold mb-2 ${subjectToMetrics.is_deal ? 'text-green-600' : 'text-red-600'}`}>
                        {subjectToMetrics.is_deal ? '✓ DEAL' : '✗ NO DEAL'}
                      </div>
                      <p className="text-sm text-slate-600">
                        Target: {subjectToDeal.reserves.target_coc_return}% Cash-on-Cash Return
                      </p>
                      <div className="mt-2">
                        <NumberInput 
                          label="Adjust Target CoC %" 
                          suffix="%" 
                          value={subjectToDeal.reserves.target_coc_return} 
                          onChange={(v) => updateDeal(setSubjectToDeal)("reserves", "target_coc_return", v)} 
                        />
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Key Metrics */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-teal-500" />
                        Key Returns
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <MetricCard 
                        label="Monthly Cash Flow" 
                        value={subjectToMetrics.monthly_cash_flow} 
                        highlight={subjectToMetrics.monthly_cash_flow > 0 ? "positive" : "negative"} 
                      />
                      <MetricCard 
                        label="Annual Cash Flow" 
                        value={subjectToMetrics.annual_cash_flow} 
                        highlight={subjectToMetrics.annual_cash_flow > 0 ? "positive" : "negative"} 
                      />
                      <MetricCard 
                        label="Cash-on-Cash Return" 
                        value={subjectToMetrics.cash_on_cash_return} 
                        isPercent 
                        isCurrency={false}
                        highlight={subjectToMetrics.cash_on_cash_return >= 10 ? "positive" : subjectToMetrics.cash_on_cash_return > 0 ? undefined : "negative"} 
                      />
                      <MetricCard label="DSCR" value={subjectToMetrics.dscr} isCurrency={false} />
                      <MetricCard label="Total Cash Invested" value={subjectToMetrics.total_cash_invested} />
                    </CardContent>
                  </Card>
                  
                  {/* Income & Expense Breakdown */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-teal-500" />
                        Monthly Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-2">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Income</p>
                        <div className="flex justify-between text-sm">
                          <span>Effective Gross Income</span>
                          <span className="font-mono text-green-600">{formatCurrency(subjectToMetrics.effective_gross_income)}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Operating Expenses</p>
                        <div className="flex justify-between text-sm">
                          <span>Total Opex</span>
                          <span className="font-mono text-red-600">-{formatCurrency(subjectToMetrics.total_operating_expenses)}</span>
                        </div>
                      </div>
                      <div className="border-t pt-2">
                        <div className="flex justify-between text-sm font-semibold">
                          <span>Net Operating Income (NOI)</span>
                          <span className="font-mono">{formatCurrency(subjectToMetrics.noi)}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Debt Service</p>
                        <div className="flex justify-between text-sm">
                          <span>Subject-To Payment</span>
                          <span className="font-mono text-red-600">-{formatCurrency(subjectToMetrics.subject_to_payment)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Seller Finance Payment</span>
                          <span className="font-mono text-red-600">-{formatCurrency(subjectToMetrics.seller_finance_payment)}</span>
                        </div>
                      </div>
                      <div className="border-t pt-2">
                        <div className="flex justify-between font-bold">
                          <span>Monthly Cash Flow</span>
                          <span className={`font-mono ${subjectToMetrics.monthly_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(subjectToMetrics.monthly_cash_flow)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Financing Summary */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-teal-500" />
                        Financing Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-2">
                      <div className="flex justify-between text-sm">
                        <span>Purchase Price</span>
                        <span className="font-mono">{formatCurrency(subjectToMetrics.purchase_price)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Subject-To Loan</span>
                        <span className="font-mono">{formatCurrency(subjectToMetrics.existing_loan_balance)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Seller Finance</span>
                        <span className="font-mono">{formatCurrency(subjectToMetrics.seller_finance_amount)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Down Payment</span>
                        <span className="font-mono">{formatCurrency(subjectToMetrics.down_payment)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between font-semibold">
                        <span>Your Equity</span>
                        <span className="font-mono text-teal-600">{formatCurrency(subjectToMetrics.total_equity)}</span>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Export Buttons */}
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-teal-500 hover:bg-teal-400 text-navy-900"
                      onClick={exportSubjectToPDF}
                      disabled={exportingPdf}
                      data-testid="subjectto-export-pdf-btn"
                    >
                      {exportingPdf ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileDown className="h-4 w-4 mr-2" />
                      )}
                      Export PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={exportSubjectToExcel}
                      disabled={exportingExcel}
                      data-testid="subjectto-export-excel-btn"
                    >
                      {exportingExcel ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Export Excel
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
